import { useForm, Controller, type SubmitErrorHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Divider,
  Grid,
  Radio,
  Stack,
  Text,
  TextInput,
  Select,
  Textarea,
  Button,
  Group,
  Table,
  ActionIcon,
  LoadingOverlay,
  Tabs,
  Accordion,
  Switch,
  Pagination,
  Badge,
  Modal,
  Checkbox,
  Avatar,
  Tooltip,
  Loader,
  ScrollArea
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import {
  IconCheck,
  IconClearAll,
  IconEdit,
  IconFileImport,
  IconPlus,
  IconSearch,
  IconTemplate,
  IconTrash,
  IconUser,
  IconUserPlus,
  IconX
} from '@tabler/icons-react';
import BreadcrumbsComponent from 'components/BreadcrumbsComponent';
import ModalPopupConfirmationComponent from 'components/ModalPopupConfirmationComponent';
import SecondaryButton from 'components/SecondaryButton';
import PrimaryButton from 'components/PrimaryButton';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { z } from 'zod';
import { Participant, Question, assessmentScheduleSchema } from '../utils/schema';
import ParticipantModal from 'components/LIA/ParticipantModal';
import QuestionOptionModal from 'components/QuestionOptionModal';
import CreateQuestionModal from 'components/QuestionOptionModal/CreateQuestionModal';
import SelectGroupQuestionModal from 'components/QuestionOptionModal/SelectGroupQuestionModal';
import { assessmentScheduleService } from 'services/lia/assessmentScheduleService';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import RespondentSelectionModal from 'components/LIA/RespondentSelectionModal';
import StandaloneRespondentModal from 'components/LIA/StandaloneRespondentModal';
import SmeSelectionModal from 'components/LIA/SmeSelectionModal';
import { Module } from '../utils/schema';
import { masterQuestionService } from 'services/lia/masterQuestionService';
import QuestionItem from 'components/LIA/QuestionItem';
import httpClient from 'services/httpClient';
import { masterAreaService } from 'services/dacum/masterAreaService';
import { notifications } from '@mantine/notifications';
import ImportQuestionModal from 'components/QuestionOptionModal/ImportQuestionModal';
import ModuleSearchModal from 'components/LIA/ModuleSearchModal';
import ImportParticipantsModal from 'components/LIA/ImportParticipantsModal';
import ModuleAssignmentModal from 'components/LIA/ModuleAssignmentModal';

const EditAssessmentSchedulePage: React.FC = () => {
  /* eslint-disable */
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [opened, { open, close }] = useDisclosure();
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [respondents, setRespondents] = useState<Participant[]>([]);
  const [initialParticipantIds, setInitialParticipantIds] = useState<Set<string>>(new Set());
  const [initialRespondentIds, setInitialRespondentIds] = useState<Set<string>>(new Set());
  const [participantSearchQuery, setParticipantSearchQuery] = useState('');
  const [respondentSearchQuery, setRespondentSearchQuery] = useState('');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeModuleTab, setActiveModuleTab] = useState<string>('');

  const [currentExternalTypeIds, setCurrentExternalTypeIds] = useState<string[]>([]);
  const [externalTypeNames, setExternalTypeNames] = useState<string[]>([]);

  const [selectedProgram, setSelectedProgram] = useState<string>('');
  const [availableModules, setAvailableModules] = useState<Module[]>([]);

  const [
    importParticipantsModalOpened,
    { open: openImportParticipantsModal, close: closeImportParticipantsModal }
  ] = useDisclosure();

  // SME related state
  const [smes, setSmes] = useState<Participant[]>([]);
  const [smeModalOpened, { open: openSmeModal, close: closeSmeModal }] = useDisclosure();

  const [currentModuleForQuestion, setCurrentModuleForQuestion] = useState<string>('');
  const [currentRespondentTypes, setCurrentRespondentTypes] = useState<string[]>([]);

  const [selectedParticipantsToAdd, setSelectedParticipantsToAdd] = useState<string[]>([]);
  const [selectedParticipantsToRemove, setSelectedParticipantsToRemove] = useState<string[]>([]);

  const [
    moduleAssignmentModalOpened,
    { open: openModuleAssignmentModal, close: closeModuleAssignmentModal }
  ] = useDisclosure();
  const [selectedParticipantForModules, setSelectedParticipantForModules] =
    useState<Participant | null>(null);

  const [participantModalOpened, { open: openParticipantModal, close: closeParticipantModal }] =
    useDisclosure();
  const [
    questionOptionModalOpened,
    { open: openQuestionOptionModal, close: closeQuestionOptionModal }
  ] = useDisclosure();
  const [
    createQuestionModalOpened,
    { open: openCreateQuestionModal, close: closeCreateQuestionModal }
  ] = useDisclosure();
  const [
    selectGroupQuestionModalOpened,
    { open: openSelectGroupQuestionModal, close: closeSelectGroupQuestionModal }
  ] = useDisclosure();
  const [
    importQuestionModalOpened,
    { open: openImportQuestionModal, close: closeImportQuestionModal }
  ] = useDisclosure(false);

  const [participantPage, setParticipantPage] = useState(1);
  const [participantPageSize, setParticipantPageSize] = useState(10);
  const [respondentPage, setRespondentPage] = useState(1);
  const [respondentPageSize, setRespondentPageSize] = useState(10);

  const [courseSource, setCourseSource] = useState<'LCMS' | 'Beelajar'>('LCMS');
  const [selectedBeelajarCourses, setSelectedBeelajarCourses] = useState<any[]>([]);
  const [beelajarParticipantsByCourse, setBeelajarParticipantsByCourse] = useState<
    Record<string, Participant[]>
  >({});

  const [moduleModalOpened, { open: openModuleModal, close: closeModuleModal }] = useDisclosure();
  const [selectedModulesData, setSelectedModulesData] = useState<any[]>([]);

  const [programSearchTerm, setProgramSearchTerm] = useState('');
  const [programPage, setProgramPage] = useState(1);
  const [programPageSize] = useState(10);
  const [debouncedSearchTerm] = useDebouncedValue(programSearchTerm, 500);
  const areaFieldRef = useRef<HTMLDivElement | null>(null);

  /* eslint-enable */

  // Loading states for data consistency
  const [isInitializing, setIsInitializing] = useState(true);
  const [isModulesLoading, setIsModulesLoading] = useState(false);
  const [isQuestionsLoading, setIsQuestionsLoading] = useState(false);
  const [isParticipantsLoading, setIsParticipantsLoading] = useState(false);
  const [isRespondentsLoading, setIsRespondentsLoading] = useState(false);

  // Filter and paginate participants
  const filteredParticipants = React.useMemo(() => {
    if (!participantSearchQuery.trim()) return participants;
    const query = participantSearchQuery.toLowerCase();
    return participants.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        p.binusianId.toLowerCase().includes(query) ||
        p.email?.toLowerCase().includes(query)
    );
  }, [participants, participantSearchQuery]);

  const paginatedParticipants = filteredParticipants.slice(
    (participantPage - 1) * participantPageSize,
    participantPage * participantPageSize
  );

  // Filter and paginate respondents
  const filteredRespondents = React.useMemo(() => {
    if (!respondentSearchQuery.trim()) return respondents;
    const query = respondentSearchQuery.toLowerCase();
    return respondents.filter(
      (r) =>
        r.name.toLowerCase().includes(query) ||
        r.binusianId.toLowerCase().includes(query) ||
        r.email?.toLowerCase().includes(query)
    );
  }, [respondents, respondentSearchQuery]);

  const paginatedRespondents = filteredRespondents.slice(
    (respondentPage - 1) * respondentPageSize,
    respondentPage * respondentPageSize
  );

  const [respondentModalOpened, { open: openRespondentModal, close: closeRespondentModal }] =
    useDisclosure();
  const [currentParticipantForRespondents, setCurrentParticipantForRespondents] =
    useState<Participant | null>(null);

  const [
    standaloneRespondentModalOpened,
    { open: openStandaloneRespondentModal, close: closeStandaloneRespondentModal }
  ] = useDisclosure();

  /* eslint-disable */
  const [isAddingParticipant, setIsAddingParticipant] = useState(true);
  const [confirmationModalOpened, { open: openConfirmationModal, close: closeConfirmationModal }] =
    useDisclosure();
  const [pendingSubmissionData, setPendingSubmissionData] = useState<any>(null);
  /* eslint-enable */

  const [modules, setModules] = useState<Module[]>([
    { moduleId: 'module1', moduleName: 'Course 1' },
    { moduleId: 'module2', moduleName: 'Course 2' }
  ]);

  const updateMutation = useMutation({
    mutationFn: (data: any) =>
      assessmentScheduleService.update({
        msAssessmentScheduleId: id!,
        ...data
      }),
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'Assessment schedule updated successfully!',
        color: 'green',
        autoClose: 3000
      });
      navigate('/app/lia/assessment-schedules');
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error?.response?.data?.message || 'Failed to update assessment schedule',
        color: 'red',
        autoClose: 5000
      });
    }
  });

  // Fetch assessment data for editing
  const {
    data: assessmentData,
    isLoading: isLoadingAssessment,
    error: assessmentError
  } = useQuery({
    queryKey: ['assessment-schedule', id],
    queryFn: () => {
      // eslint-disable-next-line no-console
      console.log('Fetching assessment detail for ID:', id);
      return assessmentScheduleService.getDetail(id!);
    },
    enabled: !!id
  });

  // Debug logging when data changes
  useEffect(() => {
    if (assessmentData) {
      // eslint-disable-next-line no-console
      console.log('Assessment data fetched successfully:', assessmentData);
    }
    if (assessmentError) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch assessment data:', assessmentError);
    }
  }, [assessmentData, assessmentError]);

  const { data: externalTypesData } = useQuery({
    queryKey: ['externalQuestionTypes'],
    queryFn: () => masterQuestionService.getExternalQuestionTypeList(),
    staleTime: 5 * 60 * 1000
  });

  const {
    data: programs,
    isLoading: isLoadingPrograms,
    isFetching: isFetchingPrograms
  } = useQuery({
    queryKey: ['programs', programPage, programPageSize, debouncedSearchTerm, selectedProgram],
    queryFn: async () => {
      const params: Record<string, string | number | undefined> = {
        PageNumber: programPage,
        PageSize: programPageSize
      };

      if (selectedProgram) {
        params.id = selectedProgram;
      } else if (debouncedSearchTerm.trim()) {
        params.search = debouncedSearchTerm.trim();
      }

      const response = await httpClient.get('/assessment-schedules/list-program', {
        params
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000
  });

  const programOptions = useMemo(() => {
    if (!Array.isArray(programs?.data)) {
      return [];
    }

    return programs.data
      .map((program: any) => {
        const value = typeof program.programId === 'string' ? program.programId : '';
        const rawName = typeof program.programName === 'string' ? program.programName.trim() : '';
        const fallbackParts = [program.jobFamily, program.description]
          .filter((text) => typeof text === 'string' && text.trim().length > 0)
          .map((text) => text.trim());
        const fallbackLabel = fallbackParts.length > 0 ? fallbackParts.join(' - ') : '';
        const fallbackId = value ? `Program ${value.slice(0, 8)}` : 'Unnamed Program';

        return {
          value,
          label: rawName || fallbackLabel || fallbackId
        };
      })
      .filter((option: { value: string }) => option.value);
  }, [programs?.data]);

  useEffect(() => {
    if (selectedProgram && programs?.data) {
      const program = programs.data.find((p: any) => p.programId === selectedProgram);
      setAvailableModules(program?.modules || []);
      setModules(program?.modules || []);
    } else {
      setAvailableModules([]);
    }
  }, [selectedProgram, programs]);

  const groupedQuestions = useMemo(() => {
    // eslint-disable-next-line no-console
    console.log('Grouping questions:', {
      questionsLength: questions.length,
      selectedModulesLength: selectedModules.length,
      selectedModules,
      questions: questions.slice(0, 2) // Log first 2 questions for debugging
    });

    const grouped: Record<string, Record<string, Record<string, Question[]>>> = {};

    questions.forEach((question) => {
      const moduleId = question.moduleId || 'unassigned';
      const respType = question.respondentType || 'unassigned';

      if (!grouped[moduleId]) {
        grouped[moduleId] = {};
      }

      if (!grouped[moduleId][respType]) {
        grouped[moduleId][respType] = {};
      }

      if (respType === 'External' && question.msExternalQuestionTypeID) {
        const typeId = question.msExternalQuestionTypeID;
        if (!grouped[moduleId][respType][typeId]) {
          grouped[moduleId][respType][typeId] = [];
        }
        grouped[moduleId][respType][typeId].push(question);
      } else {
        const defaultKey = 'default';
        if (!grouped[moduleId][respType][defaultKey]) {
          grouped[moduleId][respType][defaultKey] = [];
        }
        grouped[moduleId][respType][defaultKey].push(question);
      }
    });

    // eslint-disable-next-line no-console
    console.log('Grouped questions result:', {
      groupedKeys: Object.keys(grouped),
      grouped
    });

    return grouped;
  }, [questions, selectedModules]); // Add selectedModules as dependency

  const handleImportParticipants = (importedParticipants: any[]) => {
    const updatedParticipants = importedParticipants.map((participant) => ({
      binusianId: participant.binusianId,
      name: participant.name,
      email: participant.email,
      division: participant.division || '',
      type: participant.type || 'Imported Participant',
      listModule: participant.listModule || []
    }));

    let duplicateCount = 0;
    let newCount = 0;
    const duplicateNames: string[] = [];

    setParticipants((prev) => {
      const updatedList = [...prev];

      updatedParticipants.forEach((newParticipant) => {
        const existingIndex = updatedList.findIndex(
          (p) => p.binusianId === newParticipant.binusianId
        );

        if (existingIndex !== -1) {
          duplicateCount++;
          duplicateNames.push(newParticipant.name);

          const existingParticipant = updatedList[existingIndex];
          const existingModules = existingParticipant.listModule || [];
          const newModules = newParticipant.listModule || [];

          const mergedModules = [...new Set([...existingModules, ...newModules])];

          updatedList[existingIndex] = {
            ...existingParticipant,
            ...newParticipant,
            listModule: mergedModules
          };
        } else {
          newCount++;
          updatedList.push(newParticipant);
        }
      });

      return updatedList;
    });

    // Show notifications after state update
    setTimeout(() => {
      if (duplicateCount > 0) {
        notifications.show({
          title: 'Import: Duplicates Found',
          message: `${duplicateCount} participant(s) were already added (${duplicateNames.join(', ')}). Their data has been merged.`,
          color: 'orange',
          autoClose: 5000
        });
      }

      if (newCount > 0) {
        notifications.show({
          title: 'Import Successful',
          message: `Successfully imported ${newCount} new participant(s).`,
          color: 'green',
          autoClose: 3000
        });
      }

      if (duplicateCount > 0 && newCount === 0) {
        notifications.show({
          title: 'Import Complete',
          message: 'All imported participants were already in the list. Data has been updated.',
          color: 'yellow',
          autoClose: 4000
        });
      }
    }, 100);

    closeImportParticipantsModal();
  };

  const handleParticipantSubmit = (
    selectedParticipants: Participant[],
    selectedRespondents: Participant[]
  ) => {
    if (selectedParticipants.length > 0) {
      // Check for duplicates
      const existingParticipantIds = participants.map((p) => p.binusianId);
      const duplicateParticipants: Participant[] = [];
      const newParticipants: Participant[] = [];

      selectedParticipants.forEach((participant) => {
        if (existingParticipantIds.includes(participant.binusianId)) {
          duplicateParticipants.push(participant);
        } else {
          newParticipants.push(participant);
        }
      });

      // Show alert for duplicates
      if (duplicateParticipants.length > 0) {
        const duplicateNames = duplicateParticipants.map((p) => p.name).join(', ');
        notifications.show({
          title: 'Duplicate Participants Detected',
          message: `The following participant(s) are already added: ${duplicateNames}. They will be skipped.`,
          color: 'orange',
          autoClose: 5000
        });
      }

      // Add only new participants
      if (newParticipants.length > 0) {
        setParticipants((prev) => [...prev, ...newParticipants]);

        // Success notification
        notifications.show({
          title: 'Participants Added',
          message: `Successfully added ${newParticipants.length} new participant(s).`,
          color: 'green',
          autoClose: 3000
        });
      } else if (duplicateParticipants.length > 0) {
        // All selected participants were duplicates
        notifications.show({
          title: 'No New Participants Added',
          message: 'All selected participants are already in the list.',
          color: 'yellow',
          autoClose: 4000
        });
      }

      if (selectedRespondents.length > 0) {
        setRespondents((prev) => [...prev, ...selectedRespondents]);
      }
    }
    closeParticipantModal();
  };

  const handleParticipantsSelected = (selectedParticipants: any[]) => {
    const updatedParticipants = selectedParticipants.map((participant) => ({
      binusianId: participant.binusianId,
      name: participant.name,
      email: participant.email,
      division: '',
      type: 'Course Participant',
      listModule: participant.listModule || []
    }));

    let duplicateCount = 0;
    let newCount = 0;
    const duplicateNames: string[] = [];

    setParticipants((prev) => {
      const updatedList = [...prev];

      updatedParticipants.forEach((newParticipant) => {
        const existingIndex = updatedList.findIndex(
          (p) => p.binusianId === newParticipant.binusianId
        );

        if (existingIndex !== -1) {
          // Participant exists - merge the listModule arrays
          duplicateCount++;
          duplicateNames.push(newParticipant.name);

          const existingParticipant = updatedList[existingIndex];
          const existingModules = existingParticipant.listModule || [];
          const newModules = newParticipant.listModule || [];

          const mergedModules = [...new Set([...existingModules, ...newModules])];

          updatedList[existingIndex] = {
            ...existingParticipant,
            listModule: mergedModules
          };
        } else {
          newCount++;
          updatedList.push(newParticipant);
        }
      });

      return updatedList;
    });

    // Show notifications after state update
    setTimeout(() => {
      if (duplicateCount > 0) {
        notifications.show({
          title: 'Duplicate Participants Found',
          message: `${duplicateCount} participant(s) were already added (${duplicateNames.join(', ')}). Their modules have been merged.`,
          color: 'orange',
          autoClose: 5000
        });
      }

      if (newCount > 0) {
        notifications.show({
          title: 'Participants Added',
          message: `Successfully added ${newCount} new participant(s).`,
          color: 'green',
          autoClose: 3000
        });
      }
    }, 100);
  };

  const handleSmeSubmit = (selectedSmes: Participant[]) => {
    const uniqueNewSmes = selectedSmes.filter(
      (newSme) => !smes.some((existingSme) => existingSme.binusianId === newSme.binusianId)
    );

    if (uniqueNewSmes.length > 0) {
      setSmes((prev) => [...prev, ...uniqueNewSmes]);
    }
    closeSmeModal();
  };

  const { data: dataMasterArea, isPending: loadingMasterArea } = useQuery({
    queryKey: ['masterAreaServiceGetList'],
    queryFn: () =>
      masterAreaService.getList({
        page: 1,
        limit: 1000
      }),
    staleTime: Infinity
  });

  const handleCreateNewQuestion = (
    moduleId: string,
    respondentTypes: string[],
    externalTypeIds?: string[]
  ) => {
    setCurrentModuleForQuestion(moduleId);
    setCurrentRespondentTypes(respondentTypes);

    if (externalTypeIds && externalTypeIds.length > 0) {
      setCurrentExternalTypeIds(externalTypeIds);

      const typeNames = externalTypeIds.map((id) => {
        const typeObj = externalTypesData?.data?.find(
          (t: any) => t.msExternalQuestionTypeID === id
        );
        return typeObj?.questionExternalType || 'Unknown Type';
      });

      setExternalTypeNames(typeNames);
    } else {
      setCurrentExternalTypeIds([]);
      setExternalTypeNames([]);
    }

    closeQuestionOptionModal();
    openCreateQuestionModal();
  };

  const handleChooseFromGroup = (
    moduleId: string,
    respondentTypes: string[],
    externalTypeIds?: string[]
  ) => {
    setCurrentModuleForQuestion(moduleId);
    setCurrentRespondentTypes(respondentTypes);

    if (externalTypeIds && externalTypeIds.length > 0) {
      setCurrentExternalTypeIds(externalTypeIds);

      const typeNames = externalTypeIds.map((id) => {
        const typeObj = externalTypesData?.data?.find(
          (t: any) => t.msExternalQuestionTypeID === id
        );
        return typeObj?.questionExternalType || 'Unknown Type';
      });

      setExternalTypeNames(typeNames);
    } else {
      setCurrentExternalTypeIds([]);
      setExternalTypeNames([]);
    }

    closeQuestionOptionModal();
    openSelectGroupQuestionModal();
  };

  useEffect(() => {
    setSelectedModules([]);
  }, [selectedProgram]);

  const handleAddNewQuestion = (questionFromModal: any) => {
    currentRespondentTypes.forEach((respondentType) => {
      if (respondentType === 'External' && currentExternalTypeIds.length > 0) {
        currentExternalTypeIds.forEach((typeId) => {
          const uniqueId = `${questionFromModal.id}-${respondentType}-${typeId}`;

          const convertedQuestion: Question = {
            id: uniqueId,
            title: questionFromModal.title,
            category: questionFromModal.category || '',
            skalaCategory: questionFromModal.skalaCategory || '', // Add this line
            description: questionFromModal.description || '',
            type: questionFromModal.type,
            moduleName:
              modules.find((m) => m.moduleId === currentModuleForQuestion)?.moduleName ||
              'Default Module',
            moduleId: currentModuleForQuestion,
            groupId: questionFromModal.groupId || '',
            groupName: questionFromModal.groupName || '',
            respondentType: respondentType,
            sequence: questions.length + 1,
            msExternalQuestionTypeID: typeId,
            rangeItems: Array.isArray(questionFromModal.rangeItems)
              ? questionFromModal.rangeItems.map((item: any, index: number) => ({
                  label: item.label,
                  value: item.value,
                  sequence: index + 1
                }))
              : []
          };

          setQuestions((prev) => [...prev, convertedQuestion]);
        });
      } else {
        const convertedQuestion: Question = {
          id: questionFromModal.id + '-' + respondentType,
          title: questionFromModal.title,
          category: questionFromModal.category || '',
          skalaCategory: questionFromModal.skalaCategory || '',
          description: questionFromModal.description || '',
          type: questionFromModal.type,
          moduleName:
            modules.find((m) => m.moduleId === currentModuleForQuestion)?.moduleName ||
            'Default Module',
          moduleId: currentModuleForQuestion,
          groupId: questionFromModal.groupId || '',
          groupName: questionFromModal.groupName || '',
          respondentType: respondentType,
          sequence: questions.length + 1,
          rangeItems: Array.isArray(questionFromModal.rangeItems)
            ? questionFromModal.rangeItems.map((item: any, index: number) => ({
                label: item.label,
                value: item.value,
                sequence: index + 1
              }))
            : []
        };

        setQuestions((prev) => [...prev, convertedQuestion]);
      }
    });

    closeCreateQuestionModal();
  };

  const handleAddGroupQuestions = (selectedQuestionsFromModal: any[]) => {
    const nextSequence = questions.length + 1;
    const newQuestions: Question[] = [];

    const sortedQuestions = [...selectedQuestionsFromModal].sort((a, b) => {
      const seqA = a.sequence || 0;
      const seqB = b.sequence || 0;

      if (seqA !== seqB) {
        return seqB - seqA;
      }

      const dateA = new Date(a.dateIn || 0).getTime();
      const dateB = new Date(b.dateIn || 0).getTime();
      return dateB - dateA;
    });

    sortedQuestions.forEach((q, idx) => {
      currentRespondentTypes.forEach((respondentType) => {
        if (respondentType === 'External' && currentExternalTypeIds.length > 0) {
          currentExternalTypeIds.forEach((typeId) => {
            const uniqueId = `${q.id}-${respondentType}-${typeId}`;

            const questionObj: Question = {
              id: uniqueId,
              title: q.title,
              description: q.description || '',
              category: q.category || '',
              skalaCategory: q.skalaCategory || '', // Add this line
              type: q.type,
              moduleName:
                modules.find((m) => m.moduleId === currentModuleForQuestion)?.moduleName ||
                'Unknown Module',
              moduleId: currentModuleForQuestion,
              groupId: q.groupId || '',
              groupName: q.groupName || '',
              respondentType: respondentType,
              sequence: nextSequence + idx, // Use sorted index for proper sequence
              msExternalQuestionTypeID: typeId,
              rangeItems: Array.isArray(q.rangeItems)
                ? q.rangeItems.map((item: any, index: number) => ({
                    label: item.label,
                    value: item.value,
                    sequence: index + 1
                  }))
                : Array.isArray(q.rangeItem)
                  ? q.rangeItem.map((item: any, index: number) => ({
                      label: item.label,
                      value: item.value,
                      sequence: index + 1
                    }))
                  : []
            };

            newQuestions.push(questionObj);
          });
        } else {
          const questionObj: Question = {
            id: q.id + '-' + respondentType,
            title: q.title,
            description: q.description || '',
            category: q.category || '',
            skalaCategory: q.skalaCategory || '', // Add this line
            type: q.type,
            moduleName:
              modules.find((m) => m.moduleId === currentModuleForQuestion)?.moduleName ||
              'Unknown Module',
            moduleId: currentModuleForQuestion,
            groupId: q.groupId || '',
            groupName: q.groupName || '',
            respondentType: respondentType,
            sequence: nextSequence + idx, // Use sorted index for proper sequence
            rangeItems: Array.isArray(q.rangeItems)
              ? q.rangeItems.map((item: any, index: number) => ({
                  label: item.label,
                  value: item.value,
                  sequence: index + 1
                }))
              : Array.isArray(q.rangeItem)
                ? q.rangeItem.map((item: any, index: number) => ({
                    label: item.label,
                    value: item.value,
                    sequence: index + 1
                  }))
                : []
          };

          newQuestions.push(questionObj);
        }
      });
    });

    setQuestions((prev) => [...prev, ...newQuestions]);
    closeSelectGroupQuestionModal();
  };

  const handleRemoveQuestion = (
    questionId: string,
    respondentType: string,
    moduleId: string,
    externalTypeId?: string
  ) => {
    setQuestions((prev) => {
      const filteredQuestions = prev.filter((q) => {
        if (externalTypeId && q.respondentType === 'External') {
          return !(q.id === questionId && q.msExternalQuestionTypeID === externalTypeId);
        } else {
          return !(
            q.id === questionId &&
            q.respondentType === respondentType &&
            q.moduleId === moduleId
          );
        }
      });
      return filteredQuestions;
    });
  };

  // Remove all questions in a section (by respondent type) and optionally by External type
  const handleRemoveSectionQuestions = (
    moduleId: string,
    respondentType: string,
    externalTypeId?: string
  ) => {
    const sectionLabel = externalTypeId ? `${respondentType} (External Type)` : respondentType;
    const confirmed = window.confirm(
      `Delete all ${sectionLabel} questions for this module? This action cannot be undone.`
    );
    if (!confirmed) return;

    setQuestions((prev) =>
      prev.filter(
        (q) =>
          !(
            q.moduleId === moduleId &&
            q.respondentType === respondentType &&
            (!externalTypeId || String(q.msExternalQuestionTypeID) === String(externalTypeId))
          )
      )
    );
  };

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors }
  } = useForm<z.infer<typeof assessmentScheduleSchema>>({
    resolver: zodResolver(assessmentScheduleSchema),
    defaultValues: {
      name: '',
      type: '',
      programId: '',
      learningObjectives: '',
      description: '',
      statusKurikulum: true,
      isBeforeSchedule: true,
      isWithQuestion: 'Y' as const,
      isReset: 'N' as const,
      notificationStatus: 'pending',
      startTime: new Date(),
      endTime: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      notificationSchedule: new Date(),
      notes: '',
      source: '',
      typeAssessment: 'Team',
      timeAssessment: 'before',
      isRandom: 'N',
      courseSource: 'LCMS',
      areaID: ''
    }
  });

  const isBeforeSchedule = watch('isBeforeSchedule');
  const statusKurikulum = watch('statusKurikulum');
  const isReset = watch('isReset');
  const typeAssessment = watch('typeAssessment');
  const canModifyParticipants = isReset === 'Y' || isReset === 'R';

  const handleTypeAssessmentChange = (newType: string) => {
    if (newType !== typeAssessment) {
      setParticipants([]);
      setRespondents([]);
    }
  };

  useEffect(() => {
    const loadAssessmentData = async () => {
      // eslint-disable-next-line no-console
      console.log('Edit form useEffect triggered:', {
        assessmentData,
        hasData: !!assessmentData
      });

      if (assessmentData) {
        setIsInitializing(true);
        const data = assessmentData;
        // eslint-disable-next-line no-console
        console.log('Assessment data received:', data);

        try {
          // Treat date strings without timezone info as UTC so DateTimePicker shows local time
          const safeParseDate = (dateValue: any) => {
            if (!dateValue) return new Date();
            const normalized =
              typeof dateValue === 'string' &&
              !dateValue.endsWith('Z') &&
              !/[+-]\d{2}:\d{2}$/.test(dateValue)
                ? dateValue + 'Z'
                : dateValue;
            const parsed = new Date(normalized);
            return isNaN(parsed.getTime()) ? new Date() : parsed;
          };

          const formData: Partial<z.infer<typeof assessmentScheduleSchema>> = {
            name: data.name || '',
            type: data.type || '',
            programId: data.programID || data.programId || '', // Handle both programID and programId
            learningObjectives: data.learningObjectives || '',
            description: data.description || '',
            isWithQuestion: (data.isWithQuestion || 'Y') as 'Y' | 'N',
            isReset:
              typeof data.isReset !== 'undefined'
                ? ((data.isReset || 'N') as 'Y' | 'N' | 'R')
                : 'N',
            notificationStatus: data.notificationStatus || 'pending',
            notes: data.notes || '',
            statusKurikulum: data.statusKurikulum === true || data.statusKurikulum === true, // Handle boolean
            isBeforeSchedule: data.isBeforeSchedule === true || data.isBeforeSchedule === true, // Handle boolean
            previousScheduleId: data.previousScheduleID || data.previousScheduleId || '', // Handle both cases
            source: data.source || '',
            typeAssessment: (data.typeAssessment || 'Team') as 'Team' | 'Individu',
            startTime: safeParseDate(data.startTime),
            endTime: safeParseDate(data.endTime),
            notificationSchedule: safeParseDate(data.notificationSchedule),
            areaID: data.areaID || '',
            timeAssessment: (data.timeAssessment || 'before') as
              | 'before'
              | 'during'
              | 'after'
              | 'gap',
            isRandom: (data.isRandom || 'N') as 'Y' | 'N',
            courseSource: 'LCMS' as 'LCMS' | 'Beelajar' // Set default since this might not come from API
          };

          // eslint-disable-next-line no-console
          console.log('Form data to reset with:', formData);

          // Reset form data first
          reset(formData);
          // eslint-disable-next-line no-console
          console.log('Form reset successful');

          // Set program ID if available (needed for statusKurikulum=true case)
          if (data.programID || data.programId) {
            setSelectedProgram(data.programID || data.programId);
          }

          // Load modules data with loading state
          if (data.modules) {
            setIsModulesLoading(true);
            const moduleData = data.modules.map((m: any) => ({
              moduleId: m.moduleID,
              moduleName: m.moduleName,
              learningObjectives: m.learningObjectives,
              courseCode: m.courseCode || m.CourseCode || '',
              description: m.description || m.Description || '',
              beelajarId: m.beelajarId || m.BeelajarId || m.beelajarID || m.BeelajarID || null
            }));

            const moduleIds = moduleData.map((m: any) => m.moduleId);

            // eslint-disable-next-line no-console
            console.log('Module data processing:', {
              originalModules: data.modules,
              processedModuleData: moduleData,
              moduleIds,
              statusKurikulum: data.statusKurikulum
            });

            // For statusKurikulum=false or when modules should be directly set
            if (!data.statusKurikulum) {
              // Set all module-related state synchronously for non-curriculum modules
              setSelectedModules(moduleIds);
              setModules(moduleData);
              setSelectedModulesData(moduleData);

              // eslint-disable-next-line no-console
              console.log('Non-curriculum modules loaded directly:', {
                selectedModules: moduleIds,
                moduleData
              });
            } else {
              // For statusKurikulum=true, set selectedModulesData but let program logic handle selectedModules
              setSelectedModulesData(moduleData);
              setAvailableModules(moduleData);

              // Only set selectedModules for curriculum-based modules
              setSelectedModules(moduleIds);

              // eslint-disable-next-line no-console
              console.log('Curriculum-based modules processed:', {
                selectedModulesData: moduleData,
                availableModules: moduleData,
                selectedModules: moduleIds,
                selectedProgram: data.programID || data.programId
              });
            }

            setIsModulesLoading(false);
          } // Load participants data with loading state
          if (data.participants) {
            setIsParticipantsLoading(true);
            const participantData = data.participants.map((p: any) => ({
              binusianId: p.binusianID || p.binusianId,
              name: p.binusianName || p.name,
              email: p.binusianEmail || p.email,
              division: p.division || '',
              type: p.type === 'SPV' ? 'Supervisor' : p.type || 'Participant',
              listModule: p.listModule || []
            }));

            setParticipants(participantData);
            // Track initial participant IDs
            setInitialParticipantIds(
              new Set(participantData.map((p: Participant) => p.binusianId))
            );
            setIsParticipantsLoading(false);

            // eslint-disable-next-line no-console
            console.log('Participants loaded:', participantData);
          }

          // Load respondents data with loading state
          if (data.respondents) {
            setIsRespondentsLoading(true);
            const respondentData = data.respondents.map((r: any) => ({
              binusianId: r.binusianID || r.binusianId,
              name: r.name,
              email: r.email,
              division: r.division || '',
              type: r.respondentType === 'SPV' ? 'Supervisor' : r.respondentType || 'Peer',
              participantId: r.participantID || r.participantId || '',
              isSelfAssess: !!r.isSelfAssess,
              participants: (r.participants || []).map((p: any) => ({
                binusianId: p.binusianID || p.binusianId,
                binusianName: p.binusianName || p.name,
                isExternalStructural: !!p.isExternalStructural
              }))
            }));

            setRespondents(respondentData);
            // Track initial respondent IDs
            setInitialRespondentIds(new Set(respondentData.map((r: Participant) => r.binusianId)));
            setIsRespondentsLoading(false);

            // eslint-disable-next-line no-console
            console.log('Respondents loaded:', respondentData);
          }

          // Load questions data with loading state (most complex, do this last)
          if (data.modules && data.modules.length > 0) {
            setIsQuestionsLoading(true);
            const allQuestions: Question[] = [];
            const moduleIdsWithQuestions = new Set<string>();

            data.modules.forEach((module: any) => {
              if (module.questions && Array.isArray(module.questions)) {
                moduleIdsWithQuestions.add(module.moduleID);
                module.questions.forEach((q: any, index: number) => {
                  const convertedQuestion: Question = {
                    id: q.trAssessmentQuestionID || `${module.moduleID}-${index}`,
                    title: q.title,
                    description: q.description || '',
                    category: q.category || '',
                    skalaCategory: q.skalaCategory || '',
                    type: q.type,
                    moduleName: module.moduleName,
                    moduleId: module.moduleID, // Make sure this matches the selectedModules
                    groupId: q.groupId || '',
                    groupName: q.groupName || '',
                    respondentType:
                      q.respondentType === 'SPV' ? 'Supervisor' : q.respondentType || 'Self',
                    sequence: q.sequence || index + 1,
                    msExternalQuestionTypeID: q.msExternalQuestionTypeID || undefined,
                    rangeItems: Array.isArray(q.rangeItems)
                      ? q.rangeItems.map((item: any, itemIndex: number) => ({
                          label: item.label,
                          value: item.value,
                          sequence: item.sequence || itemIndex + 1
                        }))
                      : []
                  };
                  allQuestions.push(convertedQuestion);
                });
              }
            });

            setQuestions(allQuestions);
            setIsQuestionsLoading(false);

            // eslint-disable-next-line no-console
            console.log('Questions loaded:', {
              questionsCount: allQuestions.length,
              moduleIdsWithQuestions: Array.from(moduleIdsWithQuestions),
              sampleQuestion: allQuestions[0],
              selectedModulesAtQuestionLoad: selectedModules
            });
          } else {
            // Clear questions if no modules have questions
            setQuestions([]);
            setIsQuestionsLoading(false);
            // eslint-disable-next-line no-console
            console.log('No modules with questions found');
          }

          // Load SMEs
          if (data.smes && Array.isArray(data.smes)) {
            const smeData = data.smes.map((sme: any) => ({
              binusianId: sme.binusianID || sme.binusianId,
              name: sme.name || sme.binusianID || sme.binusianId,
              email: sme.email,
              division: sme.division || '',
              type: 'SME'
            }));

            setSmes(smeData);
            // eslint-disable-next-line no-console
            console.log('SMEs loaded:', smeData);
          }

          // Wait a bit to ensure all state updates are processed
          await new Promise((resolve) => setTimeout(resolve, 100));

          setIsInitializing(false);
          // eslint-disable-next-line no-console
          console.log('All data loading completed');
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error loading assessment data:', error);
          setIsInitializing(false);
          setIsModulesLoading(false);
          setIsQuestionsLoading(false);
          setIsParticipantsLoading(false);
          setIsRespondentsLoading(false);
        }
      }
    };

    loadAssessmentData();
  }, [assessmentData, reset, setValue, watch]);

  // Handle program-based module loading for curriculum assessments
  useEffect(() => {
    const statusKurikulum = watch('statusKurikulum');

    if (statusKurikulum && selectedProgram && selectedModulesData.length > 0) {
      // For curriculum-based assessments, ensure modules are properly set when program is available
      const moduleIds = selectedModulesData.map((m) => m.moduleId);

      // eslint-disable-next-line no-console
      console.log('Setting up curriculum modules:', {
        selectedProgram,
        selectedModulesData,
        moduleIds,
        currentSelectedModules: selectedModules
      });

      if (selectedModules.length === 0) {
        setSelectedModules(moduleIds);
        setModules(selectedModulesData);

        // eslint-disable-next-line no-console
        console.log('Updated selectedModules for curriculum assessment:', moduleIds);
      }
    }
  }, [selectedProgram, selectedModulesData, selectedModules, watch]);

  const { data: previousSchedules, isLoading: isLoadingPreviousSchedules } = useQuery({
    queryKey: ['assessmentSchedules', 'dropdown'],
    queryFn: () => assessmentScheduleService.getList({ limit: 100 }),
    enabled: !isBeforeSchedule,
    staleTime: 5 * 60 * 1000
  });

  // Format the dropdown data
  const previousScheduleOptions = previousSchedules?.data
    ? previousSchedules.data.map((schedule: any) => ({
        value: schedule.msAssessmentScheduleID,
        label: `${schedule.name} (${new Date(schedule.startTime).toLocaleDateString()})`
      }))
    : [];

  const handleAddRespondent = () => {
    openStandaloneRespondentModal();
  };

  const handleStandaloneRespondentSubmit = (selectedRespondents: Participant[]) => {
    if (selectedRespondents.length > 0) {
      const validatedRespondents: Participant[] = [];
      const rejectedRespondents: { respondent: Participant; reason: string }[] = [];

      selectedRespondents.forEach((respondent) => {
        const potentialParticipantIds = participants.map((p) => p.binusianId);

        if (potentialParticipantIds.length === 0) {
          rejectedRespondents.push({
            respondent,
            reason: 'No participants available to assess'
          });
          return;
        }

        const hasQuestions = validateRespondentHasQuestions(respondent, potentialParticipantIds);

        if (!hasQuestions) {
          rejectedRespondents.push({
            respondent,
            reason: `No ${respondent.type || 'Peer'} questions available for any participants`
          });
          return;
        }

        validatedRespondents.push(respondent);
      });

      const newRespondents: Participant[] = [];
      const respondentsToUpdate: Participant[] = [];

      validatedRespondents.forEach((newRespondent) => {
        const existingRespondent = respondents.find(
          (existing) => existing.binusianId === newRespondent.binusianId
        );

        if (existingRespondent) {
          respondentsToUpdate.push(newRespondent);
        } else {
          newRespondents.push(newRespondent);
        }
      });

      let addedCount = 0;

      if (newRespondents.length > 0) {
        const mappedRespondents = newRespondents.map((r) => {
          let type = r.type || '';
          if (type.toUpperCase() === 'SPV') {
            type = 'Supervisor';
          }

          const participants = r.participants || [];

          if (participants.length === 0 && r.participantId) {
            participants.push({
              binusianId: r.participantId,
              isExternalStructural: false
            });
          }

          return {
            ...r,
            type: ['Peer', 'Supervisor', 'Subordinate'].includes(type) ? type : 'Peer',
            isSelfAssess: false,
            participants
          };
        });

        setRespondents((prev) => [...prev, ...mappedRespondents]);
        addedCount += mappedRespondents.length;
      }

      if (respondentsToUpdate.length > 0) {
        setRespondents((prev) =>
          prev.map((existing) => {
            const update = respondentsToUpdate.find((r) => r.binusianId === existing.binusianId);

            if (update) {
              const existingParticipants = existing.participants || [];
              const newParticipants = update.participants || [];

              const mergedParticipants = [
                ...existingParticipants,
                ...newParticipants.filter(
                  (np) => !existingParticipants.some((ep) => ep.binusianId === np.binusianId)
                )
              ];

              addedCount += newParticipants.filter(
                (np) => !existingParticipants.some((ep) => ep.binusianId === np.binusianId)
              ).length;

              return {
                ...existing,
                participants: mergedParticipants
              };
            }

            return existing;
          })
        );
      }

      if (rejectedRespondents.length > 0) {
        const rejectionMessage = rejectedRespondents
          .map(
            ({ respondent, reason }) => `• ${respondent.name || respondent.binusianId}: ${reason}`
          )
          .join('\n');

        alert(`${rejectedRespondents.length} respondent(s) were not added:\n\n${rejectionMessage}`);
      }

      if (addedCount > 0) {
        alert(`Successfully added ${addedCount} assignment(s)`);
      }
    }

    closeStandaloneRespondentModal();
  };

  const handleAddRespondentsForParticipant = (participant: Participant) => {
    setCurrentParticipantForRespondents(participant);
    openRespondentModal();
  };

  const validateRespondentHasQuestions = (
    respondent: Participant,
    participantIds: string[]
  ): boolean => {
    let respondentType = respondent.type || 'Peer';
    if (respondentType.toUpperCase() === 'SPV') {
      respondentType = 'Supervisor';
    }
    if (!['Peer', 'Supervisor', 'Subordinate'].includes(respondentType)) {
      respondentType = 'Peer';
    }

    const participantModules = new Set<string>();

    participantIds.forEach((participantId) => {
      const participant = participants.find((p) => p.binusianId === participantId);
      if (participant) {
        if (statusKurikulum && participant.listModule) {
          participant.listModule.forEach((module) => {
            participantModules.add(module);
          });
        } else {
          selectedModules.forEach((moduleId) => {
            participantModules.add(moduleId);
          });
        }
      }
    });

    const hasRelevantQuestions = questions.some((question) => {
      const moduleMatch = participantModules.has(question.moduleId);
      const typeMatch = question.respondentType === respondentType;

      return moduleMatch && typeMatch;
    });

    return hasRelevantQuestions;
  };

  const handleRespondentSelection = (selectedRespondents: Participant[]) => {
    if (selectedRespondents.length > 0) {
      const validatedRespondents: Participant[] = [];
      const rejectedRespondents: { respondent: Participant; reason: string }[] = [];

      selectedRespondents.forEach((respondent) => {
        // Get the participant IDs this respondent will assess
        const participantIds =
          respondent.participants?.map((p) => p.binusianId) ||
          (currentParticipantForRespondents ? [currentParticipantForRespondents.binusianId] : []);

        if (participantIds.length === 0) {
          rejectedRespondents.push({
            respondent,
            reason: 'No participants assigned'
          });
          return;
        }

        const hasQuestions = validateRespondentHasQuestions(respondent, participantIds);

        if (!hasQuestions) {
          const participantNames = participantIds
            .map((id) => {
              const participant = participants.find((p) => p.binusianId === id);
              return participant?.name || id;
            })
            .join(', ');

          rejectedRespondents.push({
            respondent,
            reason: `No ${respondent.type || 'Peer'} questions available for participant(s): ${participantNames}`
          });
          return;
        }

        validatedRespondents.push(respondent);
      });

      const mappedRespondents = validatedRespondents.map((r) => {
        let type = r.type || '';
        if (type.toUpperCase() === 'SPV') {
          type = 'Supervisor';
        }

        return {
          ...r,
          type: ['Peer', 'Supervisor', 'Subordinate'].includes(type) ? type : 'Peer',
          participantId: r.participantId || currentParticipantForRespondents?.binusianId || '',
          isSelfAssess: false,
          participants: r.participants || [
            {
              binusianId: currentParticipantForRespondents?.binusianId || '',
              isExternalStructural: false
            }
          ]
        };
      });

      if (mappedRespondents.length > 0) {
        let addedCount = 0;
        const nextRespondents = [...respondents];

        mappedRespondents.forEach((newRespondent) => {
          const existingIndex = nextRespondents.findIndex(
            (existingRespondent) =>
              existingRespondent.binusianId === newRespondent.binusianId ||
              (!!newRespondent.email && existingRespondent.email === newRespondent.email)
          );

          if (existingIndex < 0) {
            nextRespondents.push(newRespondent);
            addedCount += newRespondent.participants?.length || 1;
            return;
          }

          const existingRespondent = nextRespondents[existingIndex];
          const existingParticipants = existingRespondent.participants || [];
          const newParticipants = newRespondent.participants || [];
          const participantsToAdd = newParticipants.filter(
            (newParticipant) =>
              !existingParticipants.some(
                (existingParticipant) =>
                  existingParticipant.binusianId === newParticipant.binusianId
              )
          );

          if (participantsToAdd.length > 0) {
            nextRespondents[existingIndex] = {
              ...existingRespondent,
              participants: [...existingParticipants, ...participantsToAdd]
            };
            addedCount += participantsToAdd.length;
          }
        });

        if (addedCount > 0) {
          setRespondents(nextRespondents);
        }
      }

      const addedRespondentAssignments = mappedRespondents.reduce((count, respondent) => {
        const existingRespondent = respondents.find(
          (existing) =>
            existing.binusianId === respondent.binusianId ||
            (!!respondent.email && existing.email === respondent.email)
        );

        if (!existingRespondent) {
          return count + (respondent.participants?.length || 1);
        }

        const existingParticipants = existingRespondent.participants || [];
        const newParticipants = respondent.participants || [];

        return (
          count +
          newParticipants.filter(
            (newParticipant) =>
              !existingParticipants.some(
                (existingParticipant) =>
                  existingParticipant.binusianId === newParticipant.binusianId
              )
          ).length
        );
      }, 0);

      // Show notification about rejected respondents
      if (rejectedRespondents.length > 0) {
        const rejectionMessage = rejectedRespondents
          .map(
            ({ respondent, reason }) => `• ${respondent.name || respondent.binusianId}: ${reason}`
          )
          .join('\n');

        alert(
          `${rejectedRespondents.length} respondent(s) were not added because they have no relevant questions to answer:\n\n${rejectionMessage}`
        );
      }

      // Show success message for added respondents
      if (addedRespondentAssignments > 0) {
        alert(`Successfully added ${addedRespondentAssignments} respondent assignment(s)`);
      }
    }

    closeRespondentModal();
  };

  const onDragEnd = (result: any, moduleId: string, respType: string, externalTypeId?: string) => {
    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;

    if (sourceIndex === destIndex) {
      return;
    }

    setQuestions((prev) => {
      let sectionQuestions;

      if (externalTypeId) {
        // Filter questions for specific external type
        sectionQuestions = prev.filter(
          (q) =>
            q.moduleId === moduleId &&
            q.respondentType === respType &&
            q.externalTypeIds?.includes(externalTypeId)
        );
      } else {
        // Filter questions for non-external types
        sectionQuestions = prev.filter(
          (q) =>
            q.moduleId === moduleId &&
            q.respondentType === respType &&
            (!q.externalTypeIds || q.externalTypeIds.length === 0)
        );
      }

      const otherQuestions = prev.filter((q) => {
        if (externalTypeId) {
          return !(
            q.moduleId === moduleId &&
            q.respondentType === respType &&
            q.externalTypeIds?.includes(externalTypeId)
          );
        } else {
          return !(
            q.moduleId === moduleId &&
            q.respondentType === respType &&
            (!q.externalTypeIds || q.externalTypeIds.length === 0)
          );
        }
      });

      const reorderedSectionQuestions = [...sectionQuestions];
      const [removed] = reorderedSectionQuestions.splice(sourceIndex, 1);
      reorderedSectionQuestions.splice(destIndex, 0, removed);

      const updatedSectionQuestions = reorderedSectionQuestions.map((q, idx) => ({
        ...q,
        sequence: idx + 1
      }));

      return [...otherQuestions, ...updatedSectionQuestions];
    });
  };

  const [
    respondentForParticipantsModalOpened,
    { open: openRespondentForParticipantsModal, close: closeRespondentForParticipantsModal }
  ] = useDisclosure();
  const [currentRespondentForParticipants, setCurrentRespondentForParticipants] =
    useState<Participant | null>(null);

  const handleAddParticipantsForRespondent = (respondent: Participant) => {
    setCurrentRespondentForParticipants(respondent);
    openRespondentForParticipantsModal();
  };

  const handleImportQuestions = (
    moduleId: string,
    respondentTypes: string[],
    externalTypeIds?: string[]
  ) => {
    setCurrentModuleForQuestion(moduleId);
    setCurrentRespondentTypes(respondentTypes);
    setCurrentExternalTypeIds(externalTypeIds || []);
    closeQuestionOptionModal();
    openImportQuestionModal();
  };

  const handleImportedQuestions = (importedQuestions: any[]) => {
    const formattedQuestions = importedQuestions.map((q) => ({
      ...q,
      moduleId: currentModuleForQuestion,
      respondentType: currentRespondentTypes[0]
    }));

    setQuestions((prevQuestions) => [...prevQuestions, ...formattedQuestions]);

    notifications.show({
      title: 'Questions Imported',
      message: `Successfully imported ${formattedQuestions.length} questions`,
      color: 'green',
      icon: <IconCheck />
    });

    closeImportQuestionModal();
  };

  const handleSelectParticipantsForRespondent = (selectedParticipants: Participant[]) => {
    if (!currentRespondentForParticipants || selectedParticipants.length === 0) return;

    setRespondents((prev) =>
      prev.map((respondent) => {
        if (respondent.binusianId === currentRespondentForParticipants.binusianId) {
          const existingParticipantIds = (respondent.participants || []).map((p) => p.binusianId);

          const newParticipants = selectedParticipants
            .filter((p) => {
              // Ensure binusianId exists before filtering
              const participantId = p.binusianId || p.participantId;
              return participantId && !existingParticipantIds.includes(participantId);
            })
            .map((p) => {
              const participantBinusianId = p.binusianId || p.participantId || '';
              return {
                binusianId: participantBinusianId,
                isExternalStructural: false
              };
            });

          return {
            ...respondent,
            participants: [...(respondent.participants || []), ...newParticipants]
          };
        }
        return respondent;
      })
    );

    closeRespondentForParticipantsModal();
  };

  useEffect(() => {
    if (respondentForParticipantsModalOpened) {
      setSelectedParticipantsToAdd([]);
      setSelectedParticipantsToRemove([]);
    }
  }, [respondentForParticipantsModalOpened]);

  const handleQuestionCategoryChange = (
    questionId: string,
    respondentType: string,
    moduleId: string,
    category: string,
    skalaCategory?: string,
    externalTypeId?: string
  ) => {
    setQuestions((prev) => {
      return prev.map((q) => {
        if (
          q.id === questionId &&
          q.respondentType === respondentType &&
          q.moduleId === moduleId &&
          (!externalTypeId ||
            (q.msExternalQuestionTypeID === externalTypeId && q.respondentType === 'External'))
        ) {
          return {
            ...q,
            category,
            skalaCategory: skalaCategory || q.skalaCategory
          };
        }
        return q;
      });
    });
  };

  const handleAddQuestion = () => {
    const coursesToCheck =
      courseSource === 'Beelajar'
        ? selectedBeelajarCourses.map((c) => c.courseID)
        : selectedModules;

    if (coursesToCheck.length === 0) {
      alert(`Please select at least one course from ${courseSource} first`);
      return;
    }

    const targetModule = activeModuleTab || coursesToCheck[0];
    setCurrentModuleForQuestion(targetModule);
    openQuestionOptionModal();
  };

  const validateBeforeSubmission = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (!watch('name')?.trim()) {
      errors.push('Schedule name is required');
    }

    if (!watch('type')) {
      errors.push('Assessment type is required');
    }

    if (statusKurikulum && !selectedProgram) {
      errors.push('Program selection is required when curriculum mode is enabled');
    }

    if (selectedModules.length === 0) {
      errors.push(`At least one ${statusKurikulum ? 'course' : 'module'} must be selected`);
    }

    if (questions.length === 0) {
      errors.push('At least one question must be added to the assessment');
    }

    // Participant validation
    if (participants.length === 0) {
      errors.push('At least one participant must be added');
    }

    // Each participant must have at least one module assigned
    if (participants.length > 0) {
      const participantsWithoutModules = participants.filter(
        (p) => !p.listModule || p.listModule.length === 0
      );
      if (participantsWithoutModules.length > 0) {
        const names = participantsWithoutModules.map((p) => p.name || p.binusianId).join(', ');
        errors.push(
          `The following participant(s) have no ${statusKurikulum ? 'courses' : 'modules'} assigned: ${names}`
        );
      }
    }

    if (selectedModules.length > 0 && questions.length > 0) {
      const modulesWithoutQuestions: string[] = [];

      selectedModules.forEach((moduleId) => {
        const moduleHasQuestions = questions.some((q) => q.moduleId === moduleId);
        if (!moduleHasQuestions) {
          const moduleName = modules.find((m) => m.moduleId === moduleId)?.moduleName || moduleId;
          modulesWithoutQuestions.push(moduleName);
        }
      });

      if (modulesWithoutQuestions.length > 0) {
        errors.push(
          `The following modules don't have any questions: ${modulesWithoutQuestions.join(', ')}`
        );
      }
    }

    if (respondents.length > 0) {
      const respondentsWithoutQuestions: string[] = [];

      respondents.forEach((respondent) => {
        const participantIds =
          respondent.participants?.map((p) => p.binusianId) ||
          (respondent.participantId ? [respondent.participantId] : []);

        if (participantIds.length > 0) {
          const hasQuestions = validateRespondentHasQuestions(respondent, participantIds);
          if (!hasQuestions) {
            respondentsWithoutQuestions.push(respondent.name || respondent.binusianId);
          }
        }
      });

      // if (respondentsWithoutQuestions.length > 0) {
      //   errors.push(
      //     `The following respondents have no relevant questions to answer: ${respondentsWithoutQuestions.join(', ')}`
      //   );
      // }
    }

    // NEW: Validate question coverage for each respondent type
    if (questions.length > 0 && participants.length > 0) {
      const respondentTypes = ['Peer', 'Supervisor', 'Subordinate'];
      const warnings: string[] = [];

      respondentTypes.forEach((respType) => {
        const hasRespondentsOfType = respondents.some((r) => {
          let type = r.type || 'Peer';
          if (type.toUpperCase() === 'SPV') type = 'Supervisor';
          return type === respType;
        });

        const hasQuestionsForType = questions.some((q) => q.respondentType === respType);

        if (hasRespondentsOfType && !hasQuestionsForType) {
          warnings.push(`You have ${respType} respondents but no ${respType} questions`);
        }
      });

      if (warnings.length > 0) {
        errors.push(...warnings);
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const handleFormError: SubmitErrorHandler<z.infer<typeof assessmentScheduleSchema>> = (
    formErrors
  ) => {
    if (formErrors.areaID) {
      notifications.show({
        title: 'Area is required',
        message: 'Please select an area before submitting this assessment schedule.',
        color: 'orange'
      });

      requestAnimationFrame(() => {
        areaFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const inputEl = areaFieldRef.current?.querySelector('input');
        inputEl?.focus();
      });
    }
  };

  const onSubmit = async () => {
    const validation = validateBeforeSubmission();

    if (!validation.isValid) {
      alert('Validation Error:\n' + validation.errors.join('\n'));
      return;
    }

    openConfirmationModal();
  };

  const processData = async () => {
    setIsSubmitting(true);

    try {
      const formData = watch();

      let moduleQuestions;

      if (courseSource === 'Beelajar') {
        moduleQuestions = selectedBeelajarCourses.map((course) => {
          const moduleQs = questions.filter((q) => q.moduleId === course.courseID);
          const courseParticipants = beelajarParticipantsByCourse[course.courseID] || [];

          return {
            moduleId: course.courseID,
            isFromBeelajar: true,
            courseName: course.courseName,
            participants: courseParticipants.map((p) => ({
              binusianId: p.binusianId,
              name: p.name,
              email: p.email,
              type: p.type
            })),
            questions: moduleQs.map((q) => {
              const questionData: any = {
                title: q.title,
                description: q.description || '',
                type: q.type,
                sequence: q.sequence || 1,
                rangeItems: q.rangeItems || [],
                respondentType: q.respondentType || 'Peer',
                category: q.category || '',
                skalaCategory: q.skalaCategory || ''
              };

              if (q.respondentType === 'External' && q.msExternalQuestionTypeID) {
                questionData.msExternalQuestionTypeID = q.msExternalQuestionTypeID;
              }

              return questionData;
            })
          };
        });
      } else {
        moduleQuestions = selectedModules.map((moduleId) => {
          const moduleQs = questions.filter((q) => q.moduleId === moduleId);

          return {
            moduleId,
            isFromBeelajar: false,
            questions: moduleQs.map((q) => {
              const questionData: any = {
                title: q.title,
                description: q.description || '',
                type: q.type,
                sequence: q.sequence || 1,
                rangeItems: q.rangeItems || [],
                respondentType: q.respondentType || 'Peer',
                category: q.category || '',
                skalaCategory: q.skalaCategory || ''
              };

              if (q.respondentType === 'External' && q.msExternalQuestionTypeID) {
                questionData.msExternalQuestionTypeID = q.msExternalQuestionTypeID;
              }

              return questionData;
            })
          };
        });
      }

      const submissionData = {
        name: formData.name,
        type: formData.type,
        programId: formData.programId,
        learningObjectives: formData.learningObjectives,
        description: formData.description,
        isWithQuestion: formData.isWithQuestion,
        isReset: formData.isReset || 'N',
        notificationStatus: formData.notificationStatus,
        notes: formData.notes || '',
        statusKurikulum: formData.statusKurikulum,
        isBeforeSchedule: formData.isBeforeSchedule,
        previousScheduleId: formData.previousScheduleId || undefined,
        courseSource: courseSource,
        areaID: formData.areaID,
        startTime: formData.startTime.toISOString(),
        endTime: formData.endTime.toISOString(),
        notificationSchedule: formData.notificationSchedule.toISOString(),
        source: formData.source || '',
        typeAssessment: formData.typeAssessment || 'Team',
        timeAssessment: formData.timeAssessment || 'before',
        isRandom: formData.isRandom || 'N',
        modules: moduleQuestions,
        participants: participants.map((p) => ({
          binusianId: p.binusianId,
          listModule: p.listModule || []
        })),
        respondents: respondents.map((r) => {
          const mappedParticipants = Array.isArray(r.participants)
            ? r.participants.map((p) => {
                // Handle both binusianID (from API) and binusianId (from newly added)
                const participantBinusianId = p.binusianId || p.binusianID;
                const result = {
                  binusianId: participantBinusianId,
                  isExternalStructural: !!p.isExternalStructural
                };
                return result;
              })
            : [];

          return {
            binusianId: r.binusianId,
            participantId: r.participantId || '',
            respondentType: r.type || '',
            division: r.division || '',
            isSelfAssess: !!r.isSelfAssess,
            participants: mappedParticipants
          };
        }),
        smes: smes.map((sme) => ({
          binusianId: sme.binusianId,
          email: sme.email || ''
        }))
      };

      await updateMutation.mutateAsync(submissionData);
      close();
    } catch {
      // Handle error
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    setParticipantPage(1);
  }, [participants.length]);

  useEffect(() => {
    setRespondentPage(1);
  }, [respondents.length]);

  /* eslint-disable */
  const {
    data: previousScheduleDetails,
    isLoading: isLoadingPreviousDetails,
    refetch: refetchPreviousDetails
  } = useQuery({
    queryKey: ['previousScheduleDetails', watch('previousScheduleId')],
    queryFn: () => {
      const scheduleId = watch('previousScheduleId');
      if (!scheduleId) return null;
      return assessmentScheduleService.getDetail(scheduleId);
    },
    enabled: !!watch('previousScheduleId') && !isBeforeSchedule,
    staleTime: 5 * 60 * 1000
  });
  /* eslint-enable */

  useEffect(() => {
    if (previousScheduleDetails && !isLoadingPreviousDetails && !isBeforeSchedule) {
      const assessment = Array.isArray(previousScheduleDetails.data)
        ? previousScheduleDetails.data[0]
        : previousScheduleDetails.data;

      if (!assessment) return;

      reset({
        name: `Copy of ${assessment.name}`,
        type: assessment.type || '',
        programId: assessment.programID || assessment.programId || '',
        learningObjectives: assessment.learningObjectives || '',
        description: assessment.description || '',
        statusKurikulum: Boolean(assessment.statusKurikulum),
        isBeforeSchedule: false,
        isWithQuestion: assessment.isWithQuestion || 'Y',
        notificationStatus: 'pending',
        startTime: new Date(),
        endTime: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        notificationSchedule: new Date(),
        notes: assessment.notes || '',
        previousScheduleId: watch('previousScheduleId'),
        source: assessment.source || '',
        typeAssessment: assessment.typeAssessment || 'Team'
      });

      // Set modules
      if (Array.isArray(assessment.modules)) {
        setSelectedModules(
          assessment.modules.map((module: any) => module.moduleID || module.moduleId || '')
        );
      }

      // Set participants
      if (Array.isArray(assessment.participants)) {
        setParticipants(
          assessment.participants.map((p: any) => ({
            binusianId: p.binusianID || p.binusianId || '',
            name: p.name || p.binusianID || p.binusianId || '',
            division: p.division || 'Unknown',
            email: p.email || '',
            type: 'Participant'
          }))
        );
      }

      // Set respondents
      if (Array.isArray(assessment.respondents)) {
        setRespondents(
          assessment.respondents.map((r: any) => ({
            binusianId: r.binusianID || r.binusianId || '',
            name: r.name || r.binusianID || r.binusianId || '',
            division: r.division || '',
            email: r.email || '',
            type: r.respondentType || 'Unknown',
            participantId: r.participantID || r.participantId || '',
            isSelfAssess: !!r.isSelfAssess,
            participants: (r.participants || []).map((p: any) => ({
              binusianId: p.binusianID || p.binusianId,
              binusianName: p.binusianName || p.name,
              isExternalStructural: !!p.isExternalStructural
            }))
          }))
        );
      }

      // Set SMEs if applicable
      if (Array.isArray(assessment.smes)) {
        setSmes(
          assessment.smes.map((sme: any) => ({
            binusianId: sme.binusianID || sme.binusianId || '',
            name: sme.name || sme.binusianID || sme.binusianId || '',
            division: sme.division || '',
            email: sme.email || '',
            type: 'SME'
          }))
        );
      }

      // Set questions
      if (Array.isArray(assessment.modules)) {
        const allQuestions: Question[] = [];

        assessment.modules.forEach((module: any) => {
          const moduleId = module.moduleID || module.moduleId || '';
          const moduleName =
            modules.find((m) => m.moduleId === moduleId)?.moduleName || 'Unknown Module';

          if (Array.isArray(module.questions)) {
            module.questions.forEach((q: any) => {
              allQuestions.push({
                id: q.msQuestionID || q.id || Date.now().toString() + Math.random(),
                title: q.title || '',
                description: q.description || '',
                type: q.type || '',
                moduleId: moduleId,
                moduleName: moduleName,
                groupId: q.msGroupQuestionID || q.groupId || '',
                groupName: q.groupName || '',
                respondentType: q.respondentType || 'Peer',
                sequence: q.sequence || 1,
                rangeItems: Array.isArray(q.rangeItems)
                  ? q.rangeItems.map((item: any) => ({
                      label: item.label || '',
                      value: item.value || '',
                      sequence: item.sequence || 0
                    }))
                  : []
              });
            });
          }
        });

        setQuestions(allQuestions);
      }
    }
  }, [previousScheduleDetails, isLoadingPreviousDetails, isBeforeSchedule, reset, modules, watch]);

  useEffect(() => {
    if (isBeforeSchedule) {
      reset({
        name: '',
        type: '',
        programId: '',
        learningObjectives: '',
        description: '',
        statusKurikulum: true,
        isBeforeSchedule: true,
        isWithQuestion: 'Y' as const,
        notificationStatus: 'pending',
        startTime: new Date(),
        endTime: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        notificationSchedule: new Date(),
        notes: '',
        source: '',
        typeAssessment: 'Team'
      });

      setSelectedModules([]);
      setParticipants([]);
      setRespondents([]);
      setSmes([]);
      setQuestions([]);
    }
  }, [isBeforeSchedule, reset]);

  const handleModulesSelected = (modules: any[], mode: 'add' | 'replace' = 'replace') => {
    if (mode === 'add') {
      // Add new modules to existing ones, avoiding duplicates
      setSelectedModulesData((prev) => {
        const existingIds = prev.map((m) => m.moduleId);
        const newModules = modules.filter((m) => !existingIds.includes(m.moduleId));
        return [...prev, ...newModules];
      });

      setSelectedModules((prev) => {
        const newIds = modules.map((m) => m.moduleId).filter((id) => !prev.includes(id));
        return [...prev, ...newIds];
      });

      setAvailableModules((prev) => {
        const existingIds = prev.map((m) => m.moduleId);
        const newModules = modules.filter((m) => !existingIds.includes(m.moduleId));
        return [...prev, ...newModules];
      });

      setModules((prev) => {
        const existingIds = prev.map((m) => m.moduleId);
        const newModules = modules.filter((m) => !existingIds.includes(m.moduleId));
        return [...prev, ...newModules];
      });
    } else {
      setSelectedModulesData(modules);
      setSelectedModules(modules.map((m) => m.moduleId));
      setAvailableModules(modules);
      setModules(modules);
    }
  };

  useEffect(() => {
    if (statusKurikulum) {
      if (selectedProgram && programs?.data && Array.isArray(programs.data)) {
        const program = programs.data.find((p: any) => p.programId === selectedProgram);
        const modules = program?.modules || [];
        setAvailableModules(Array.isArray(modules) ? modules : []);
        setModules(Array.isArray(modules) ? modules : []);
      } else {
        setAvailableModules([]);
        setModules([]);
      }
    } else {
      setAvailableModules(selectedModulesData);
      setModules(selectedModulesData);
    }
  }, [selectedProgram, programs, statusKurikulum, selectedModulesData]);

  const generateAssessmentDescription = () => {
    const header = `Hi, this assessment is designed to evaluate your learning progress and understanding of the selected ${statusKurikulum ? 'courses' : 'modules'}. Please complete this assessment thoughtfully and honestly.

The assessment covers the following ${statusKurikulum ? 'courses' : 'modules'}:`;

    const modulesList = selectedModulesData
      .map(
        (module, index) =>
          `${index + 1}. ${module.moduleName}${module.courseCode ? ` (${module.courseCode})` : ''}`
      )
      .join('\n');

    const footer = `

Thank you for your cooperation and participation in this assessment. Your responses will help us improve the quality of education and ensure you receive the best learning experience possible.

Please complete the assessment by the specified deadline. If you have any questions or technical issues, please contact your instructor or the academic support team.

Good luck with your assessment!`;

    return selectedModulesData.length > 0 ? `${header}\n\n${modulesList}${footer}` : '';
  };

  const updateDescriptionTemplate = () => {
    const template = generateAssessmentDescription();
    if (template) {
      setValue('description', template);
    }
  };

  useEffect(() => {
    const currentDescription = watch('description');
    if (!currentDescription && selectedModulesData.length > 0) {
      const template = generateAssessmentDescription();
      if (template) {
        setValue('description', template);
      }
    }
  }, [selectedModulesData, watch, setValue]);

  const handleModuleAssignmentSubmit = (updatedParticipants: Participant[]) => {
    if (selectedParticipantForModules) {
      // Single mode: merge the one updated participant back into the full list
      setParticipants((prev) =>
        prev.map((p) => {
          const updated = updatedParticipants.find((u) => u.binusianId === p.binusianId);
          return updated ? { ...p, listModule: updated.listModule } : p;
        })
      );
    } else {
      // Bulk mode: replace entire list
      setParticipants(updatedParticipants);
    }
    closeModuleAssignmentModal();
    setSelectedParticipantForModules(null);
  };

  if (!id) {
    return (
      <Stack gap='xl' px={20}>
        <BreadcrumbsComponent
          items={[
            { title: 'Assessment Schedule', href: '/app/lia/assessment-schedules' },
            { title: 'Edit Schedule', href: '' }
          ]}
        />
        <Text c='red'>Assessment ID is required for editing.</Text>
      </Stack>
    );
  }

  return (
    <Stack gap='xl' px={20}>
      <BreadcrumbsComponent
        items={[
          { title: 'Assessment Schedule', href: '/app/lia/assessment-schedules' },
          { title: 'Edit Schedule', href: '' }
        ]}
      />

      <div style={{ position: 'relative' }}>
        <LoadingOverlay
          visible={
            isSubmitting ||
            updateMutation.isPending ||
            isLoadingAssessment ||
            isInitializing ||
            isModulesLoading ||
            isQuestionsLoading ||
            isParticipantsLoading ||
            isRespondentsLoading
          }
        />

        <form onSubmit={handleSubmit(onSubmit, handleFormError)}>
          <Stack gap='lg'>
            <Grid>
              <Grid.Col span={12}>
                <Controller
                  name='statusKurikulum'
                  control={control}
                  render={({ field }) => (
                    <Radio.Group
                      label='Status Kurikulum'
                      onChange={(value) => field.onChange(value === 'true')}
                      value={String(field.value)}
                    >
                      <Group mt='xs'>
                        <Radio value='true' label='Yes' />
                        <Radio value='false' label='No' />
                      </Group>
                    </Radio.Group>
                  )}
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <Controller
                  name='isBeforeSchedule'
                  control={control}
                  render={({ field }) => (
                    <Radio.Group
                      label='Status Assessment'
                      onChange={(value) => field.onChange(value === 'true')}
                      value={String(field.value)}
                    >
                      <Group mt='xs'>
                        <Radio value='true' label='Initial' />
                        <Radio value='false' label='Follow Up' />
                      </Group>
                    </Radio.Group>
                  )}
                />
              </Grid.Col>
            </Grid>
            {!isBeforeSchedule && (
              <Controller
                name='previousScheduleId'
                control={control}
                render={({ field }) => (
                  <Select
                    label='Previous Schedule'
                    placeholder={
                      isLoadingPreviousSchedules
                        ? 'Loading schedules...'
                        : 'Select previous assessment'
                    }
                    data={previousScheduleOptions}
                    error={errors.previousScheduleId?.message}
                    searchable
                    maxDropdownHeight={280}
                    disabled={isLoadingPreviousSchedules}
                    {...field}
                  />
                )}
              />
            )}
            <Controller
              name='name'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Schedule Name'
                  placeholder='Assessment Product XYZ'
                  error={errors.name?.message}
                  {...field}
                />
              )}
            />
            <Controller
              name='type'
              control={control}
              render={({ field }) => (
                <Select
                  label='Select Assessment Type'
                  placeholder='Select type'
                  data={[
                    { value: 'product', label: 'Product' },
                    { value: 'non-product', label: 'Non-Product' }
                  ]}
                  error={errors.type?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name='timeAssessment'
              control={control}
              render={({ field }) => (
                <Select
                  label='Select Assessment Time'
                  placeholder='Select time'
                  data={[
                    { value: 'before', label: 'Before' },
                    { value: 'during', label: 'During' },
                    { value: 'after', label: 'After' },
                    { value: 'gap', label: 'Gap' }
                  ]}
                  error={errors.type?.message}
                  {...field}
                />
              )}
            />
            <div ref={areaFieldRef}>
              <Controller
                name='areaID'
                control={control}
                render={({ field }) => (
                  <Select
                    label='Area'
                    placeholder='Select area'
                    data={
                      dataMasterArea?.data?.map((item: any) => ({
                        value: item.areaID,
                        label: item.areaName
                      })) || []
                    }
                    disabled={loadingMasterArea}
                    error={errors.areaID?.message}
                    value={field.value}
                    onChange={(value) => field.onChange(value || '')}
                    required
                  />
                )}
              />
            </div>

            {/* Reset Mode Control */}
            <Divider my='md' />
            <Controller
              name='isReset'
              control={control}
              render={({ field }) => (
                <Radio.Group
                  label='Assessment Mode'
                  description='Choose how much you can modify this assessment schedule'
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.isReset?.message}
                  mb='md'
                >
                  <Stack gap='xs' mt='xs'>
                    <Radio
                      value='Y'
                      label={
                        <div>
                          <Text fw={'bold'} c='green'>
                            Reset Assessment
                          </Text>
                          <Text size='xs' c='dimmed'>
                            This action will{' '}
                            <b>
                              permanently delete all participant and respondent submissions
                              (responses)
                            </b>
                            . A new assessment email will be sent to all participants and
                            respondents after you save the changes to this assessment.
                          </Text>
                        </div>
                      }
                    />
                    <Radio
                      value='R'
                      label={
                        <div>
                          <Text fw={'bold'} c='blue'>
                            Add/Delete Participants, Respondents, and Questions Assessment
                          </Text>
                          <Text size='xs' c='dimmed'>
                            This action will <b>send a notification email only</b> to the newly
                            added participants or respondents after you save the changes to the
                            assessment.
                          </Text>
                        </div>
                      }
                    />
                    <Radio
                      value='N'
                      label={
                        <div>
                          <Text fw={500} c='red'>
                            Change Assessment Details
                          </Text>
                          <Text size='xs' c='dimmed'>
                            This action <b>will not send any email notification</b> to existing or
                            new participants/respondents after you save the assessment detail
                            changes.
                          </Text>
                        </div>
                      }
                    />
                  </Stack>
                </Radio.Group>
              )}
            />

            {courseSource === 'LCMS' && (
              <>
                {statusKurikulum ? (
                  <>
                    <Controller
                      name='programId'
                      control={control}
                      render={({ field }) => (
                        <Stack gap='xs'>
                          <Select
                            label='Program Name'
                            placeholder='Search and select program'
                            data={programOptions}
                            error={errors.programId?.message}
                            value={selectedProgram || ''}
                            onChange={(value) => {
                              setSelectedProgram(value || '');
                              field.onChange(value || '');
                              setProgramSearchTerm('');
                              setProgramPage(1);
                              setSelectedModules([]);
                              setSelectedModulesData([]);
                            }}
                            disabled={isLoadingPrograms || isReset !== 'Y'}
                            searchable
                            clearable
                            onSearchChange={(query) => {
                              setProgramSearchTerm(query);
                              setProgramPage(1);
                            }}
                            searchValue={programSearchTerm}
                            maxDropdownHeight={200}
                            rightSection={isFetchingPrograms ? <Loader size='xs' /> : undefined}
                          />

                          {/* Add pagination controls below the select if needed */}
                          {programs?.totalPages && programs.totalPages > 1 && (
                            <Group justify='center' gap='xs'>
                              <Button
                                variant='subtle'
                                size='xs'
                                onClick={() => setProgramPage((prev) => Math.max(1, prev - 1))}
                                disabled={programPage === 1 || isLoadingPrograms}
                              >
                                Previous
                              </Button>
                              <Text size='xs' c='dimmed'>
                                Page {programPage} of {programs.totalPages}
                              </Text>
                              <Button
                                variant='subtle'
                                size='xs'
                                onClick={() => setProgramPage((prev) => prev + 1)}
                                disabled={programPage >= programs.totalPages || isLoadingPrograms}
                              >
                                Next
                              </Button>
                            </Group>
                          )}
                        </Stack>
                      )}
                    />

                    <Stack gap='xs'>
                      <Group justify='space-between' align='flex-end'>
                        <Text size='sm' fw={550}>
                          Selected Courses
                          <Text component='span' c='red'>
                            {' '}
                            *
                          </Text>
                        </Text>
                        <Button
                          variant='outline'
                          size='sm'
                          leftSection={<IconPlus size={16} />}
                          onClick={openModuleModal}
                          disabled={!selectedProgram || isReset !== 'Y'}
                        >
                          {!selectedProgram
                            ? 'Select Program First'
                            : isReset !== 'Y'
                              ? 'Edit Mode Required'
                              : 'Search & Add Courses'}
                        </Button>
                      </Group>

                      {selectedModulesData.length > 0 ? (
                        <Stack gap='xs'>
                          {selectedModulesData.map((module, index) => (
                            <div
                              key={module.moduleId}
                              style={{
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #e9ecef',
                                borderRadius: '8px',
                                padding: '12px 16px',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                                cursor: 'default'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#e9ecef';
                                e.currentTarget.style.borderColor = '#ced4da';
                                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.1)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#f8f9fa';
                                e.currentTarget.style.borderColor = '#e9ecef';
                                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                              }}
                            >
                              <Group justify='space-between' align='flex-start'>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <Group gap='xs' align='center' mb='xs'>
                                    <div
                                      style={{
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '50%',
                                        backgroundColor: '#228be6',
                                        color: 'white',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      {index + 1}
                                    </div>
                                    <Text size='sm' fw={600} style={{ color: '#495057' }}>
                                      {module.moduleName}
                                    </Text>
                                  </Group>

                                  {module.courseCode && (
                                    <Group gap='xs' mb='xs'>
                                      <Badge
                                        variant='light'
                                        color='blue'
                                        size='sm'
                                        style={{ fontWeight: 500 }}
                                      >
                                        {module.courseCode}
                                      </Badge>
                                    </Group>
                                  )}

                                  {module.description && (
                                    <Text
                                      size='xs'
                                      c='dimmed'
                                      lineClamp={2}
                                      style={{
                                        lineHeight: '1.4',
                                        marginTop: '4px'
                                      }}
                                    >
                                      {module.description}
                                    </Text>
                                  )}
                                </div>

                                <ActionIcon
                                  variant='subtle'
                                  color='red'
                                  size='md'
                                  onClick={() => {
                                    const updatedModules = selectedModulesData.filter(
                                      (m) => m.moduleId !== module.moduleId
                                    );
                                    handleModulesSelected(updatedModules);
                                  }}
                                  style={{ marginLeft: '12px' }}
                                  disabled={isReset !== 'Y'}
                                >
                                  <IconX size={16} />
                                </ActionIcon>
                              </Group>
                            </div>
                          ))}
                        </Stack>
                      ) : (
                        <Text
                          size='sm'
                          c='dimmed'
                          ta='center'
                          py='md'
                          style={{ border: '1px dashed #ced4da', borderRadius: '4px' }}
                        >
                          {selectedProgram
                            ? 'No courses selected. Click "Search & Add Courses" to select courses.'
                            : 'Select a program first, then choose courses.'}
                        </Text>
                      )}
                    </Stack>
                  </>
                ) : (
                  <Stack gap='xs'>
                    <Group justify='space-between' align='flex-end'>
                      <Text size='sm' fw={500}>
                        Selected Modules
                        <Text component='span' c='red'>
                          {' '}
                          *
                        </Text>
                      </Text>
                      <Button
                        variant='outline'
                        size='sm'
                        leftSection={<IconPlus size={16} />}
                        onClick={openModuleModal}
                        disabled={isReset !== 'Y'}
                      >
                        {isReset !== 'Y' ? 'Edit Mode Required' : 'Search & Add Modules'}
                      </Button>
                    </Group>

                    {selectedModulesData.length > 0 ? (
                      <div
                        style={{ border: '1px solid #e9ecef', borderRadius: '4px', padding: '8px' }}
                      >
                        {selectedModulesData.map((module, index) => (
                          <Group
                            key={module.moduleId}
                            justify='space-between'
                            mb={index < selectedModulesData.length - 1 ? 'xs' : 0}
                          >
                            <div>
                              <Text size='sm' fw={500}>
                                {module.moduleName}
                              </Text>
                              {module.courseCode && (
                                <Text size='xs' c='dimmed'>
                                  {module.courseCode}
                                </Text>
                              )}
                              {module.description && (
                                <Text size='xs' c='dimmed' lineClamp={1}>
                                  {module.description}
                                </Text>
                              )}
                            </div>
                            <ActionIcon
                              variant='light'
                              color='red'
                              size='sm'
                              onClick={() => {
                                const updatedModules = selectedModulesData.filter(
                                  (m) => m.moduleId !== module.moduleId
                                );
                                handleModulesSelected(updatedModules);
                              }}
                              disabled={isReset !== 'Y'}
                            >
                              <IconX size={14} />
                            </ActionIcon>
                          </Group>
                        ))}
                      </div>
                    ) : (
                      <Text
                        size='sm'
                        c='dimmed'
                        ta='center'
                        py='md'
                        style={{ border: '1px dashed #ced4da', borderRadius: '4px' }}
                      >
                        No modules selected. Click "Search & Add Modules" to select modules.
                      </Text>
                    )}

                    {(!selectedModules || selectedModules.length === 0) && (
                      <Text size='xs' c='red'>
                        At least one module must be selected
                      </Text>
                    )}
                  </Stack>
                )}
              </>
            )}

            <Divider my='md' />

            <Text size='lg' fw={600}>
              Questions
            </Text>
            <Text c='dimmed' size='sm' style={{ marginTop: '-10px' }}>
              Add questions to the assessment
            </Text>

            <Button
              variant='outline'
              leftSection={<IconPlus size={16} />}
              onClick={handleAddQuestion}
              fullWidth
              disabled={
                selectedModulesData.length === 0 ||
                isReset === 'N' ||
                isLoadingAssessment ||
                isInitializing ||
                isModulesLoading ||
                isQuestionsLoading
              }
              loading={isQuestionsLoading || isModulesLoading}
            >
              Add Question
            </Button>

            {selectedModulesData.length === 0 &&
              !isLoadingAssessment &&
              !isInitializing &&
              !isModulesLoading && (
                <Text c='red' size='sm'>
                  Please select at least one {statusKurikulum ? 'course' : 'module'} before adding
                  questions
                </Text>
              )}

            {isReset === 'N' && selectedModulesData.length > 0 && (
              <Text c='red' size='sm'>
                Cannot modify questions in Read Only mode
              </Text>
            )}

            {/* Show loading state for questions */}
            {(isQuestionsLoading || isModulesLoading || isInitializing) && (
              <Group justify='center' p='md'>
                <Loader size='sm' />
                <Text size='sm' c='dimmed'>
                  Loading {isModulesLoading ? 'modules' : isQuestionsLoading ? 'questions' : 'data'}
                  ...
                </Text>
              </Group>
            )}

            {selectedModulesData.length > 0 &&
              !isQuestionsLoading &&
              !isModulesLoading &&
              !isInitializing && (
                <Tabs defaultValue={selectedModules[0]} mt='md'>
                  <Tabs.List style={{ flexWrap: 'wrap', gap: 'var(--mantine-spacing-xs)' }}>
                    {selectedModules.map((moduleId) => (
                      <Tabs.Tab
                        key={moduleId}
                        value={moduleId}
                        style={{ flex: '1 1 200px', maxWidth: '100%' }}
                      >
                        <Text
                          size='sm'
                          fw={500}
                          style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                        >
                          {modules.find((m) => m.moduleId === moduleId)?.moduleName || moduleId}
                        </Text>
                      </Tabs.Tab>
                    ))}
                  </Tabs.List>

                  {selectedModules.map((moduleId) => {
                    const moduleQuestions = groupedQuestions[moduleId] || {};

                    // eslint-disable-next-line no-console
                    console.log('Rendering module:', {
                      moduleId,
                      moduleQuestions,
                      moduleQuestionsKeys: Object.keys(moduleQuestions),
                      groupedQuestionsKeys: Object.keys(groupedQuestions)
                    });

                    return (
                      <Tabs.Panel key={moduleId} value={moduleId} pt='md'>
                        {Object.keys(moduleQuestions).length === 0 ? (
                          <Text c='dimmed' size='sm'>
                            No questions added for this {statusKurikulum ? 'course' : 'module'} yet
                          </Text>
                        ) : (
                          <Accordion defaultValue={Object.keys(moduleQuestions)[0]} mt='md'>
                            {Object.keys(moduleQuestions).map((respType) => {
                              if (Object.keys(moduleQuestions[respType]).length === 0) {
                                return null;
                              }

                              return (
                                <Accordion.Item key={respType} value={respType}>
                                  <Accordion.Control>
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                      }}
                                    >
                                      <Text fw={500} size='md'>
                                        {respType} Assessment Questions
                                      </Text>
                                      <Tooltip
                                        label={`Delete all ${respType} questions in this ${statusKurikulum ? 'course' : 'module'}`}
                                      >
                                        <ActionIcon
                                          color='red'
                                          variant='subtle'
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveSectionQuestions(moduleId, respType);
                                          }}
                                          disabled={isReset !== 'Y'}
                                        >
                                          <IconTrash size={16} />
                                        </ActionIcon>
                                      </Tooltip>
                                    </div>
                                  </Accordion.Control>

                                  <Accordion.Panel>
                                    {respType === 'External' ? (
                                      <Accordion
                                        defaultValue={Object.keys(moduleQuestions[respType])[0]}
                                        mt='md'
                                      >
                                        {Object.keys(moduleQuestions[respType]).map(
                                          (externalTypeId) => {
                                            const externalTypeQuestions =
                                              moduleQuestions[respType][externalTypeId] || [];

                                            if (externalTypeQuestions.length === 0) {
                                              return null;
                                            }

                                            const externalTypeObj = externalTypesData?.data?.find(
                                              (t: any) =>
                                                t.msExternalQuestionTypeID === externalTypeId
                                            );
                                            const externalTypeName =
                                              externalTypeObj?.questionExternalType ||
                                              'Unknown External Type';

                                            return (
                                              <Accordion.Item
                                                key={externalTypeId}
                                                value={externalTypeId}
                                              >
                                                <Accordion.Control>
                                                  <div
                                                    style={{
                                                      display: 'flex',
                                                      justifyContent: 'space-between',
                                                      alignItems: 'center'
                                                    }}
                                                  >
                                                    <Text fw={500} size='sm' color='blue'>
                                                      External Type: {externalTypeName}
                                                    </Text>
                                                    <Tooltip
                                                      label={`Delete all External (${externalTypeName}) questions`}
                                                    >
                                                      <ActionIcon
                                                        color='red'
                                                        variant='subtle'
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleRemoveSectionQuestions(
                                                            moduleId,
                                                            respType,
                                                            externalTypeId
                                                          );
                                                        }}
                                                        disabled={isReset !== 'Y'}
                                                      >
                                                        <IconTrash size={16} />
                                                      </ActionIcon>
                                                    </Tooltip>
                                                  </div>
                                                </Accordion.Control>
                                                <Accordion.Panel>
                                                  <DragDropContext
                                                    onDragEnd={(result) =>
                                                      onDragEnd(
                                                        result,
                                                        moduleId,
                                                        respType,
                                                        externalTypeId
                                                      )
                                                    }
                                                  >
                                                    <Droppable
                                                      droppableId={`droppable-${respType}-${moduleId}-${externalTypeId}`}
                                                    >
                                                      {(provided) => (
                                                        <div
                                                          {...provided.droppableProps}
                                                          ref={provided.innerRef}
                                                        >
                                                          {externalTypeQuestions.map(
                                                            (question, index) => (
                                                              <QuestionItem
                                                                key={question.id}
                                                                question={question}
                                                                index={index}
                                                                moduleId={moduleId}
                                                                respType={respType}
                                                                externalTypeId={externalTypeId}
                                                                onRemove={handleRemoveQuestion}
                                                                onCategoryChange={
                                                                  handleQuestionCategoryChange
                                                                }
                                                                isReset={isReset}
                                                              />
                                                            )
                                                          )}
                                                          {provided.placeholder}
                                                        </div>
                                                      )}
                                                    </Droppable>
                                                  </DragDropContext>
                                                </Accordion.Panel>
                                              </Accordion.Item>
                                            );
                                          }
                                        )}
                                      </Accordion>
                                    ) : (
                                      <DragDropContext
                                        onDragEnd={(result) =>
                                          onDragEnd(result, moduleId, respType)
                                        }
                                      >
                                        <Droppable
                                          droppableId={`droppable-${respType}-${moduleId}`}
                                        >
                                          {(provided) => (
                                            <div
                                              {...provided.droppableProps}
                                              ref={provided.innerRef}
                                            >
                                              {moduleQuestions[respType]['default']?.map(
                                                (question, index) => (
                                                  <QuestionItem
                                                    key={question.id}
                                                    question={question}
                                                    index={index}
                                                    moduleId={moduleId}
                                                    respType={respType}
                                                    onRemove={handleRemoveQuestion}
                                                    onCategoryChange={handleQuestionCategoryChange}
                                                    isReset={isReset}
                                                  />
                                                )
                                              )}
                                              {provided.placeholder}
                                            </div>
                                          )}
                                        </Droppable>
                                      </DragDropContext>
                                    )}
                                  </Accordion.Panel>
                                </Accordion.Item>
                              );
                            })}
                          </Accordion>
                        )}
                      </Tabs.Panel>
                    );
                  })}
                </Tabs>
              )}

            <Divider my='md' />

            <Text size='lg' fw={600}>
              Participants & Respondents
            </Text>
            <Text
              c='dimmed'
              size='sm'
              style={{
                marginTop: '-10px'
              }}
            >
              Add participants and respondents to the assessment schedule
            </Text>
            <Controller
              name='typeAssessment'
              control={control}
              render={({ field }) => (
                <Select
                  label='Assessment Team Type'
                  placeholder='Select team type'
                  data={[
                    { value: 'Team', label: 'Team' },
                    { value: 'Individu', label: 'Individual' }
                  ]}
                  error={errors.typeAssessment?.message}
                  {...field}
                  onChange={(value) => {
                    if (value) {
                      handleTypeAssessmentChange(value);
                      field.onChange(value);
                    }
                  }}
                />
              )}
            />
            <Stack gap='md'>
              <Group justify='space-between'>
                <Text size='sm' fw={600}>
                  Participants ({participants.length})
                </Text>
                <Group gap='xs'>
                  <Button
                    variant='outline'
                    size='sm'
                    leftSection={<IconUserPlus size={16} />}
                    onClick={openParticipantModal}
                    disabled={
                      questions.length === 0 ||
                      isReset === 'N' ||
                      isQuestionsLoading ||
                      isParticipantsLoading ||
                      isInitializing
                    }
                    loading={isParticipantsLoading}
                  >
                    Add Participant
                  </Button>
                  <Button
                    variant='outline'
                    size='sm'
                    leftSection={<IconFileImport size={16} />}
                    onClick={openImportParticipantsModal}
                    disabled={
                      questions.length === 0 ||
                      isReset === 'N' ||
                      isQuestionsLoading ||
                      isParticipantsLoading ||
                      isInitializing
                    }
                    loading={isParticipantsLoading}
                  >
                    Import Participants
                  </Button>
                </Group>
              </Group>

              {participants.length > 0 ? (
                <>
                  <TextInput
                    placeholder='Search participants by name or ID...'
                    leftSection={<IconSearch size={16} />}
                    value={participantSearchQuery}
                    onChange={(e) => setParticipantSearchQuery(e.currentTarget.value)}
                    mb='md'
                  />
                  <ScrollArea offsetScrollbars>
                    <Table striped withTableBorder highlightOnHover style={{ minWidth: 720 }}>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Participant</Table.Th>
                          <Table.Th>Email</Table.Th>
                          <Table.Th>Type</Table.Th>
                          {watch('typeAssessment') === 'Individu' && (
                            <Table.Th>Respondents</Table.Th>
                          )}
                          <Table.Th>
                            {statusKurikulum ? 'Assigned Courses' : 'Assigned Modules'}
                          </Table.Th>
                          <Table.Th style={{ width: 120 }}>Actions</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {paginatedParticipants.map((participant) => {
                          const isNewlyAdded = !initialParticipantIds.has(participant.binusianId);
                          return (
                            <Table.Tr key={participant.binusianId}>
                              <Table.Td>
                                <Group>
                                  <div>
                                    <Group gap='xs'>
                                      <Text size='sm' fw={500}>
                                        {participant.name}
                                      </Text>
                                      {isNewlyAdded && (
                                        <Badge color='green' size='sm' variant='light'>
                                          Added
                                        </Badge>
                                      )}
                                    </Group>
                                    <Text size='xs' c='dimmed'>
                                      {participant.binusianId}
                                    </Text>
                                  </div>
                                </Group>
                              </Table.Td>
                              <Table.Td>{participant.email || 'No email available'}</Table.Td>
                              <Table.Td>{participant.type}</Table.Td>
                              {watch('typeAssessment') === 'Individu' && (
                                <Table.Td>
                                  {(() => {
                                    const assignedRespondents = respondents.filter((respondent) =>
                                      respondent.participants?.some(
                                        (p) => p.binusianId === participant.binusianId
                                      )
                                    );

                                    if (assignedRespondents.length === 0) {
                                      return (
                                        <Group gap={4}>
                                          <Text size='xs' c='dimmed'>
                                            No respondents
                                          </Text>
                                          <Tooltip label='Assign respondents'>
                                            <ActionIcon
                                              size='xs'
                                              color='blue'
                                              variant='light'
                                              onClick={() =>
                                                handleAddRespondentsForParticipant(participant)
                                              }
                                            >
                                              <IconPlus size={12} />
                                            </ActionIcon>
                                          </Tooltip>
                                        </Group>
                                      );
                                    }

                                    return (
                                      <Group gap={5} wrap='wrap'>
                                        {assignedRespondents.map((respondent) => (
                                          <Badge
                                            key={respondent.binusianId}
                                            color='blue'
                                            variant='light'
                                            size='sm'
                                            style={{
                                              whiteSpace: 'normal',
                                              wordBreak: 'break-word',
                                              textAlign: 'left'
                                            }}
                                          >
                                            {respondent.name}
                                          </Badge>
                                        ))}
                                      </Group>
                                    );
                                  })()}
                                </Table.Td>
                              )}
                              <Table.Td>
                                {participant.listModule && participant.listModule.length > 0 ? (
                                  <Group gap={5} wrap='wrap'>
                                    {participant.listModule.map((moduleId) => {
                                      const module = availableModules.find(
                                        (m) => m.moduleId === moduleId
                                      );
                                      return (
                                        <Badge
                                          key={moduleId}
                                          color='blue'
                                          size='sm'
                                          style={{
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                            textAlign: 'left'
                                          }}
                                        >
                                          {module?.moduleName || moduleId}
                                        </Badge>
                                      );
                                    })}
                                  </Group>
                                ) : (
                                  <Group gap={4}>
                                    <Text size='xs' c='red'>
                                      No {statusKurikulum ? 'courses' : 'modules'} assigned
                                    </Text>
                                    <Tooltip
                                      label={`Assign ${statusKurikulum ? 'courses' : 'modules'}`}
                                    >
                                      <ActionIcon
                                        size='xs'
                                        color='blue'
                                        variant='light'
                                        onClick={() => {
                                          setSelectedParticipantForModules(participant);
                                          openModuleAssignmentModal();
                                        }}
                                      >
                                        <IconPlus size={12} />
                                      </ActionIcon>
                                    </Tooltip>
                                  </Group>
                                )}
                              </Table.Td>
                              <Table.Td>
                                <Group gap='xs'>
                                  <Tooltip
                                    label={`Edit ${statusKurikulum ? 'course' : 'module'} assignments`}
                                  >
                                    <ActionIcon
                                      color='blue'
                                      variant='light'
                                      onClick={() => {
                                        setSelectedParticipantForModules(participant);
                                        openModuleAssignmentModal();
                                      }}
                                      title='Edit Assignments'
                                    >
                                      <IconEdit size={18} />
                                    </ActionIcon>
                                  </Tooltip>

                                  {/* Add Respondents Button */}
                                  <ActionIcon
                                    color='#7B2183'
                                    variant='light'
                                    onClick={() => handleAddRespondentsForParticipant(participant)}
                                    title='Add Respondents'
                                  >
                                    <IconUserPlus size={18} />
                                  </ActionIcon>

                                  {/* Remove Participant Button */}
                                  <ActionIcon
                                    color='red'
                                    variant='light'
                                    onClick={() => {
                                      setParticipants((prev) =>
                                        prev.filter((p) => p.binusianId !== participant.binusianId)
                                      );

                                      setRespondents((prev) =>
                                        prev.map((respondent) => ({
                                          ...respondent,
                                          participants: (respondent.participants || []).filter(
                                            (participantRef) =>
                                              participantRef.binusianId !== participant.binusianId
                                          )
                                        }))
                                      );
                                    }}
                                    title='Remove Participant'
                                    disabled={!canModifyParticipants}
                                  >
                                    <IconTrash size={18} />
                                  </ActionIcon>
                                </Group>
                              </Table.Td>
                            </Table.Tr>
                          );
                        })}
                      </Table.Tbody>
                    </Table>
                  </ScrollArea>

                  {/* Participant Pagination Controls */}
                  {filteredParticipants.length > participantPageSize && (
                    <Group justify='space-between' mt='md'>
                      <Text size='sm' c='dimmed'>
                        Showing {(participantPage - 1) * participantPageSize + 1} to{' '}
                        {Math.min(
                          participantPage * participantPageSize,
                          filteredParticipants.length
                        )}{' '}
                        of {filteredParticipants.length} participants
                      </Text>
                      <Group gap='sm'>
                        <Text size='sm'>Page size:</Text>
                        <Select
                          value={participantPageSize.toString()}
                          onChange={(value) => {
                            const newSize = parseInt(value || '10');
                            setParticipantPageSize(newSize);
                            setParticipantPage(1);
                          }}
                          data={[
                            { value: '5', label: '5' },
                            { value: '10', label: '10' },
                            { value: '20', label: '20' },
                            { value: '50', label: '50' }
                          ]}
                          style={{ width: 80 }}
                        />

                        <Pagination
                          total={Math.ceil(participants.length / participantPageSize)}
                          value={participantPage}
                          onChange={setParticipantPage}
                          siblings={1}
                          boundaries={1}
                        />
                      </Group>
                    </Group>
                  )}
                </>
              ) : (
                <Text c='dimmed' ta='center' py='xl'>
                  No participants added yet. Please choose the course to get the participant.
                </Text>
              )}
              {questions.length === 0 && (
                <Text c='red' size='sm' mt='xs'>
                  Please add questions first before adding participants
                </Text>
              )}
              {isReset === 'N' && questions.length > 0 && (
                <Text c='red' size='sm' mt='xs'>
                  Cannot add participants in Read Only mode
                </Text>
              )}
            </Stack>
            <Stack gap='md'>
              <Group justify='space-between'>
                <Text size='sm' fw={600}>
                  Respondent ({respondents.length})
                </Text>
                <Button
                  variant='outline'
                  size='sm'
                  leftSection={<IconUserPlus size={16} />}
                  onClick={handleAddRespondent}
                  disabled={
                    participants.length === 0 ||
                    questions.length === 0 ||
                    isReset === 'N' ||
                    isQuestionsLoading ||
                    isParticipantsLoading ||
                    isRespondentsLoading ||
                    isInitializing
                  }
                  loading={isRespondentsLoading}
                >
                  Add More Respondent
                </Button>
              </Group>
              {respondents.length > 0 ? (
                <>
                  <TextInput
                    placeholder='Search respondents by name or ID...'
                    leftSection={<IconSearch size={16} />}
                    value={respondentSearchQuery}
                    onChange={(e) => setRespondentSearchQuery(e.currentTarget.value)}
                    mb='md'
                  />
                  <ScrollArea offsetScrollbars>
                    <Table striped withTableBorder highlightOnHover style={{ minWidth: 720 }}>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Respondent</Table.Th>
                          <Table.Th>Email</Table.Th>
                          <Table.Th>Type</Table.Th>
                          <Table.Th>Participants to Assess</Table.Th>
                          <Table.Th>Self Assessment</Table.Th>
                          <Table.Th style={{ width: 120 }}>Actions</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {paginatedRespondents.map((respondent) => {
                          const isNewlyAdded = !initialRespondentIds.has(respondent.binusianId);
                          return (
                            <Table.Tr key={respondent.binusianId}>
                              <Table.Td>
                                <Group>
                                  <div>
                                    <Group gap='xs'>
                                      <Text size='sm' fw={500}>
                                        {respondent.name}
                                      </Text>
                                      {isNewlyAdded && (
                                        <Badge color='green' size='sm' variant='light'>
                                          Added
                                        </Badge>
                                      )}
                                    </Group>
                                    <Text size='xs' c='dimmed'>
                                      {respondent.binusianId}
                                    </Text>
                                  </div>
                                </Group>
                              </Table.Td>
                              <Table.Td>{respondent.email}</Table.Td>
                              <Table.Td>{respondent.type}</Table.Td>
                              <Table.Td>
                                <Group gap={5} wrap='wrap'>
                                  {(respondent.participants || []).map((participantRef) => {
                                    // First try to get name from the participantRef itself (from API)
                                    const participantName =
                                      participantRef.binusianName ||
                                      participantRef.name ||
                                      participants.find(
                                        (p) => p.binusianId === participantRef.binusianId
                                      )?.name ||
                                      'Unknown';

                                    return (
                                      <Badge
                                        key={participantRef.binusianId}
                                        color={
                                          participantRef.isExternalStructural ? 'orange' : 'blue'
                                        }
                                        variant='light'
                                        size='sm'
                                        style={{
                                          whiteSpace: 'normal',
                                          wordBreak: 'break-word',
                                          textAlign: 'left'
                                        }}
                                      >
                                        {participantName}
                                      </Badge>
                                    );
                                  })}
                                  {(!respondent.participants ||
                                    respondent.participants.length === 0) && (
                                    <Group gap={4}>
                                      <Text size='xs' c='dimmed'>
                                        None
                                      </Text>
                                      <Tooltip label='Assign participants to assess'>
                                        <ActionIcon
                                          size='xs'
                                          color='blue'
                                          variant='light'
                                          onClick={() =>
                                            handleAddParticipantsForRespondent(respondent)
                                          }
                                        >
                                          <IconPlus size={12} />
                                        </ActionIcon>
                                      </Tooltip>
                                    </Group>
                                  )}
                                </Group>
                              </Table.Td>
                              <Table.Td>
                                <Switch
                                  checked={!!respondent.isSelfAssess}
                                  onChange={(event) => {
                                    setRespondents((prev) =>
                                      prev.map((r) =>
                                        r.binusianId === respondent.binusianId
                                          ? { ...r, isSelfAssess: event.currentTarget.checked }
                                          : r
                                      )
                                    );
                                  }}
                                  color='#7B2183'
                                />
                              </Table.Td>
                              <Table.Td>
                                <Group gap='xs'>
                                  <ActionIcon
                                    color='#7B2183'
                                    variant='light'
                                    onClick={() => handleAddParticipantsForRespondent(respondent)}
                                    title='Add participants to assess'
                                  >
                                    <IconUserPlus size={18} />
                                  </ActionIcon>
                                  <ActionIcon
                                    color='red'
                                    onClick={() =>
                                      setRespondents((prev) =>
                                        prev.filter((r) => r.binusianId !== respondent.binusianId)
                                      )
                                    }
                                    disabled={!canModifyParticipants}
                                    title='Remove Respondent'
                                  >
                                    <IconTrash size={16} />
                                  </ActionIcon>
                                </Group>
                              </Table.Td>
                            </Table.Tr>
                          );
                        })}
                      </Table.Tbody>
                    </Table>
                  </ScrollArea>

                  <Group justify='space-between'>
                    <Select
                      value={respondentPageSize.toString()}
                      onChange={(value) => {
                        const newSize = parseInt(value || '5', 10);
                        setParticipantPageSize(newSize);
                        setParticipantPage(1);
                      }}
                      data={[
                        { value: '5', label: '5' },
                        { value: '10', label: '10' },
                        { value: '20', label: '20' },
                        { value: '50', label: '50' }
                      ]}
                      style={{ width: 80 }}
                    />

                    <Pagination
                      total={Math.ceil(filteredRespondents.length / respondentPageSize)}
                      value={respondentPage}
                      onChange={setRespondentPage}
                      siblings={1}
                      boundaries={1}
                    />
                  </Group>
                </>
              ) : (
                <Text c='dimmed' ta='center' py='xl'>
                  No respondent added yet. Click "Add More Respondent" to get started.
                </Text>
              )}
              {questions.length === 0 && (
                <Text c='red' size='sm' mt='xs'>
                  Please add questions first before adding respondents
                </Text>
              )}

              {participants.length === 0 && questions.length > 0 && (
                <Text c='red' size='sm' mt='xs'>
                  Please add participants first before adding respondents
                </Text>
              )}

              {isReset === 'N' && questions.length > 0 && participants.length > 0 && (
                <Text c='red' size='sm' mt='xs'>
                  Cannot add respondents in Read Only mode
                </Text>
              )}
            </Stack>
            {watch('type') === 'product' && (
              <>
                <Divider my='md' />

                <Text size='lg' fw={600}>
                  Subject Matter Experts (SMEs)
                </Text>
                <Text
                  c='dimmed'
                  size='sm'
                  style={{
                    marginTop: '-10px'
                  }}
                >
                  Add expert reviewers to assess the quality
                </Text>

                <Button
                  variant='outline'
                  leftSection={<IconUser size={16} />}
                  onClick={openSmeModal}
                  fullWidth
                >
                  Add Subject Matter Expert
                </Button>

                {smes.length > 0 && (
                  <ScrollArea offsetScrollbars>
                    <Table striped withTableBorder highlightOnHover style={{ minWidth: 600 }}>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Expert</Table.Th>
                          <Table.Th>Email</Table.Th>
                          <Table.Th>Division</Table.Th>
                          <Table.Th style={{ width: 60 }}></Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {smes.map((sme) => (
                          <Table.Tr key={sme.binusianId}>
                            <Table.Td>
                              <Group>
                                <div>
                                  <Text size='sm' fw={500}>
                                    {sme.name}
                                  </Text>
                                  <Text size='xs' c='dimmed'>
                                    {sme.binusianId}
                                  </Text>
                                </div>
                              </Group>
                            </Table.Td>
                            <Table.Td>{sme.email || 'No email available'}</Table.Td>
                            <Table.Td>{sme.division || 'N/A'}</Table.Td>
                            <Table.Td>
                              <ActionIcon
                                color='red'
                                onClick={() =>
                                  setSmes((prev) =>
                                    prev.filter((s) => s.binusianId !== sme.binusianId)
                                  )
                                }
                              >
                                <IconTrash size={16} />
                              </ActionIcon>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </ScrollArea>
                )}
              </>
            )}
            <Divider my='md' />

            <Controller
              name='isRandom'
              control={control}
              render={({ field }) => (
                <Select
                  label='Are Questions Shuffled to Respondent'
                  placeholder='Select model'
                  data={[
                    { value: 'Y', label: 'Yes' },
                    { value: 'N', label: 'No' }
                  ]}
                  error={errors.type?.message}
                  {...field}
                />
              )}
            />
            <Divider my='md' />
            {/* Schedule Timing */}
            <Grid>
              <Grid.Col span={6}>
                <Controller
                  name='startTime'
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      label='Schedule Start Date & Time'
                      placeholder='Select date and time'
                      error={errors.startTime?.message}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <Controller
                  name='endTime'
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      label='Schedule End Date & Time'
                      placeholder='Select date and time'
                      error={errors.endTime?.message}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Grid.Col>
            </Grid>
            {/* Notification Settings */}
            {/* <Controller
              name='notificationStatus'
              control={control}
              render={({ field }) => (
                <Select
                  label='Notification Status'
                  placeholder='Select notification status'
                  data={[
                    { value: 'pending', label: 'Pending' },
                    { value: 'sent', label: 'Sent' },
                    { value: 'disabled', label: 'Disabled' }
                  ]}
                  error={errors.notificationStatus?.message}
                  {...field}
                />
              )}
            /> */}
            {/* <Controller
              name='notificationSchedule'
              control={control}
              render={({ field }) => (
                <DateTimePicker
                  label='Notification Schedule'
                  placeholder='Select date and time for notifications'
                  error={errors.notificationSchedule?.message}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            /> */}
            <Stack gap='xs'>
              <Group justify='space-between' align='flex-end'>
                <Text size='sm' fw={500}>
                  About this assessment
                </Text>
                <Group gap='xs'>
                  <Button
                    size='xs'
                    variant='outline'
                    leftSection={<IconTemplate size={14} />}
                    onClick={updateDescriptionTemplate}
                    disabled={selectedModulesData.length === 0}
                  >
                    Generate Template
                  </Button>
                  <Button
                    size='xs'
                    variant='outline'
                    color='red'
                    leftSection={<IconClearAll size={14} />}
                    onClick={() => setValue('description', '')}
                  >
                    Clear
                  </Button>
                </Group>
              </Group>

              <Controller
                name='description'
                control={control}
                render={({ field }) => (
                  <Textarea
                    placeholder='Enter what you think about this assessment...'
                    autosize
                    minRows={18}
                    maxRows={36}
                    styles={{ input: { resize: 'vertical' } }}
                    error={errors.description?.message}
                    {...field}
                  />
                )}
              />

              {selectedModulesData.length === 0 && (
                <Text size='xs' c='orange'>
                  Select {statusKurikulum ? 'courses' : 'modules'} first to generate a template
                </Text>
              )}
            </Stack>
            {/* Notes */}
            <Controller
              name='notes'
              control={control}
              render={({ field }) => (
                <Textarea label='Note' placeholder='Additional notes...' minRows={3} {...field} />
              )}
            />
            {/* Form Actions */}
            <Group mt='xl'>
              <SecondaryButton
                text='Cancel'
                onClick={() => {
                  navigate('/app/lia/assessment-schedules?search=');
                }}
              />
              <PrimaryButton text='Update Assessment Schedule' type='submit' />
            </Group>
          </Stack>
        </form>
      </div>

      <ModuleSearchModal
        opened={moduleModalOpened}
        onClose={closeModuleModal}
        onModulesSelected={handleModulesSelected}
        onParticipantsSelected={handleParticipantsSelected}
        selectedModuleIds={selectedModules}
        title={statusKurikulum ? 'Search & Select Courses' : 'Search & Select Modules'}
        programId={statusKurikulum ? selectedProgram : undefined}
        statusKurikulum={statusKurikulum}
        programModulesData={
          statusKurikulum
            ? programs?.data?.find((p: any) => p.programId === selectedProgram)?.modules || []
            : undefined
        }
      />

      <Modal
        opened={respondentForParticipantsModalOpened}
        onClose={closeRespondentForParticipantsModal}
        title={`Manage participants for ${currentRespondentForParticipants?.name || 'Respondent'} to assess`}
        size='lg'
      >
        <Stack>
          <div>
            <Text fw={600} mb='xs'>
              Currently Assigned Participants
            </Text>
            {currentRespondentForParticipants?.participants &&
            currentRespondentForParticipants.participants.length > 0 ? (
              <>
                <Table striped>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Participant</Table.Th>
                      <Table.Th style={{ width: 100 }}>Remove</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {currentRespondentForParticipants.participants.map((participantRef) => {
                      const participantName =
                        participantRef.binusianName ||
                        participantRef.name ||
                        participants.find((p) => p.binusianId === participantRef.binusianId)
                          ?.name ||
                        'Unknown';

                      const participantData = {
                        name: participantName,
                        binusianId: participantRef.binusianId
                      };

                      const isSelectedToRemove = selectedParticipantsToRemove.includes(
                        participantRef.binusianId
                      );

                      return (
                        <Table.Tr
                          key={participantRef.binusianId}
                          style={isSelectedToRemove ? { backgroundColor: '#FFEEEE' } : {}}
                        >
                          <Table.Td>
                            <Group>
                              <Avatar color='#7B2183' radius='xl'>
                                {participantData.name && participantData.name.length > 0
                                  ? participantData.name.charAt(0)
                                  : '?'}
                              </Avatar>
                              <div>
                                <Text size='sm' fw={500}>
                                  {participantData.name}
                                </Text>
                                <Text size='xs' c='dimmed'>
                                  {participantRef.binusianId}
                                </Text>
                              </div>
                            </Group>
                          </Table.Td>
                          <Table.Td>
                            <Checkbox
                              checked={isSelectedToRemove}
                              onChange={(e) => {
                                if (e.currentTarget.checked) {
                                  setSelectedParticipantsToRemove([
                                    ...selectedParticipantsToRemove,
                                    participantRef.binusianId
                                  ]);
                                } else {
                                  setSelectedParticipantsToRemove(
                                    selectedParticipantsToRemove.filter(
                                      (id) => id !== participantRef.binusianId
                                    )
                                  );
                                }
                              }}
                            />
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>

                <Button
                  mt='sm'
                  color='red'
                  leftSection={<IconTrash size={16} />}
                  variant='outline'
                  disabled={selectedParticipantsToRemove.length === 0}
                  onClick={() => {
                    if (selectedParticipantsToRemove.length > 0) {
                      setRespondents((prev) =>
                        prev.map((respondent) => {
                          if (
                            respondent.binusianId === currentRespondentForParticipants?.binusianId
                          ) {
                            const updatedRespondent = {
                              ...respondent,
                              participants: (respondent.participants || []).filter(
                                (p) => !selectedParticipantsToRemove.includes(p.binusianId)
                              )
                            };
                            // Update the current respondent state to reflect changes in UI
                            setCurrentRespondentForParticipants(updatedRespondent);
                            return updatedRespondent;
                          }
                          return respondent;
                        })
                      );
                      setSelectedParticipantsToRemove([]);
                    }
                  }}
                >
                  Remove Selected Participants ({selectedParticipantsToRemove.length})
                </Button>
              </>
            ) : (
              <Text c='dimmed' ta='center'>
                No participants assigned yet.
              </Text>
            )}
          </div>

          <Divider my='md' />

          <div>
            <Text fw={600} mb='xs'>
              Available Participants to Assign
            </Text>
            {participants.length === 0 ? (
              <Text c='dimmed' ta='center'>
                No participants available. Please add participants first.
              </Text>
            ) : (
              <>
                <Table striped>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Participant</Table.Th>
                      <Table.Th style={{ width: 100 }}>Select</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {participants.map((participant) => {
                      const isAlreadyAssigned =
                        currentRespondentForParticipants?.participants?.some(
                          (p) => p.binusianId === participant.binusianId
                        );

                      const isSelectedToAdd = selectedParticipantsToAdd.includes(
                        participant.binusianId
                      );

                      if (
                        isAlreadyAssigned &&
                        !selectedParticipantsToRemove.includes(participant.binusianId)
                      ) {
                        return null;
                      }

                      return (
                        <Table.Tr
                          key={participant.binusianId}
                          style={isSelectedToAdd ? { backgroundColor: '#EEFFEE' } : {}}
                        >
                          <Table.Td>
                            <Group>
                              <Avatar color='#7B2183' radius='xl'>
                                {participant.name && participant.name.length > 0
                                  ? participant.name.charAt(0)
                                  : '?'}
                              </Avatar>
                              <div>
                                <Text size='sm' fw={500}>
                                  {participant.name}
                                </Text>
                                <Text size='xs' c='dimmed'>
                                  {participant.binusianId}
                                </Text>
                                {isAlreadyAssigned && (
                                  <Badge color='orange' size='xs'>
                                    Removed - Available to reassign
                                  </Badge>
                                )}
                              </div>
                            </Group>
                          </Table.Td>
                          <Table.Td>
                            <Checkbox
                              checked={isSelectedToAdd}
                              onChange={(e) => {
                                if (e.currentTarget.checked) {
                                  setSelectedParticipantsToAdd([
                                    ...selectedParticipantsToAdd,
                                    participant.binusianId
                                  ]);
                                } else {
                                  setSelectedParticipantsToAdd(
                                    selectedParticipantsToAdd.filter(
                                      (id) => id !== participant.binusianId
                                    )
                                  );
                                }
                              }}
                            />
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>

                <Group justify='flex-end' mt='md'>
                  <Button
                    variant='outline'
                    onClick={closeRespondentForParticipantsModal}
                    color='gray'
                  >
                    Cancel
                  </Button>

                  <Button
                    color='#7B2183'
                    leftSection={<IconUserPlus size={16} />}
                    disabled={selectedParticipantsToAdd.length === 0}
                    onClick={() => {
                      if (selectedParticipantsToAdd.length > 0) {
                        const selectedParticipantsData = participants.filter((p) =>
                          selectedParticipantsToAdd.includes(p.binusianId)
                        );

                        handleSelectParticipantsForRespondent(selectedParticipantsData);
                        setSelectedParticipantsToAdd([]);
                      }
                    }}
                  >
                    Add Selected Participants ({selectedParticipantsToAdd.length})
                  </Button>
                </Group>
              </>
            )}
          </div>
        </Stack>
      </Modal>

      <ImportParticipantsModal
        opened={importParticipantsModalOpened}
        onClose={closeImportParticipantsModal}
        onSubmit={handleImportParticipants}
        availableModules={availableModules}
        statusKurikulum={statusKurikulum}
      />

      <ModuleAssignmentModal
        opened={moduleAssignmentModalOpened}
        onClose={() => {
          closeModuleAssignmentModal();
          setSelectedParticipantForModules(null);
        }}
        onSubmit={handleModuleAssignmentSubmit}
        participants={
          selectedParticipantForModules ? [selectedParticipantForModules] : participants
        }
        availableModules={availableModules}
        statusKurikulum={statusKurikulum}
        mode={selectedParticipantForModules ? 'single' : 'multiple'}
        selectedParticipant={selectedParticipantForModules}
      />

      <ModalPopupConfirmationComponent
        buttonText='Update Schedule'
        opened={confirmationModalOpened}
        statusProcess='edit'
        open={openConfirmationModal}
        close={closeConfirmationModal}
        onSubmit={processData}
      />

      <SmeSelectionModal
        opened={smeModalOpened}
        onClose={closeSmeModal}
        existingSmes={smes}
        onSubmit={handleSmeSubmit}
      />

      <ParticipantModal
        opened={participantModalOpened}
        onClose={closeParticipantModal}
        onSubmit={handleParticipantSubmit}
        availableModules={availableModules}
        statusKurikulum={statusKurikulum}
        selectedModuleIds={selectedModules}
      />

      <QuestionOptionModal
        opened={questionOptionModalOpened}
        onClose={closeQuestionOptionModal}
        onSelectCreateNew={handleCreateNewQuestion}
        onSelectFromGroup={handleChooseFromGroup}
        onSelectImport={handleImportQuestions}
        selectedModules={selectedModules}
        modules={modules}
      />

      <ImportQuestionModal
        opened={importQuestionModalOpened}
        onClose={closeImportQuestionModal}
        onSubmit={handleImportedQuestions}
        moduleId={currentModuleForQuestion}
        moduleName={modules.find((m) => m.moduleId === currentModuleForQuestion)?.moduleName || ''}
        respondentTypes={currentRespondentTypes}
        externalTypeIds={currentExternalTypeIds}
        externalTypeNames={externalTypeNames}
      />

      <CreateQuestionModal
        opened={createQuestionModalOpened}
        onClose={closeCreateQuestionModal}
        onSubmit={handleAddNewQuestion}
        moduleId={currentModuleForQuestion}
        moduleName={modules.find((m) => m.moduleId === currentModuleForQuestion)?.moduleName || ''}
        respondentTypes={currentRespondentTypes}
        externalTypeIds={currentExternalTypeIds}
        externalTypeNames={externalTypeNames}
      />

      <SelectGroupQuestionModal
        opened={selectGroupQuestionModalOpened}
        onClose={closeSelectGroupQuestionModal}
        onSubmit={handleAddGroupQuestions}
        moduleId={currentModuleForQuestion}
        moduleName={modules.find((m) => m.moduleId === currentModuleForQuestion)?.moduleName || ''}
        respondentTypes={currentRespondentTypes}
        externalTypeIds={currentExternalTypeIds}
        externalTypeNames={externalTypeNames}
      />

      <RespondentSelectionModal
        opened={respondentModalOpened}
        onClose={closeRespondentModal}
        participant={
          currentParticipantForRespondents || { binusianId: '', name: '', type: '', division: '' }
        }
        existingRespondents={respondents}
        onSubmit={handleRespondentSelection}
      />

      <StandaloneRespondentModal
        opened={standaloneRespondentModalOpened}
        onClose={closeStandaloneRespondentModal}
        existingRespondents={respondents}
        onSubmit={handleStandaloneRespondentSubmit}
        participants={participants}
      />
    </Stack>
  );
};

export default EditAssessmentSchedulePage;
